import React, { useState, useEffect, useRef, useCallback } from "react";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import { styled, keyframes } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import axios from "axios";
import QcCard from "./components/autentificare/QcCard";
import {
  getAdditionalActReadiness,
  isValidAdditionalActAmount,
} from "./components/autentificare/additionalActReadiness";
import {
  getAdditionalActWorkflow,
  getAdditionalActFailureFeedback,
  isAdditionalActSubmitted,
} from "./components/autentificare/additionalActWorkflow";
import useAdditionalActPreview from "./components/autentificare/useAdditionalActPreview";
import ManagementStockCard from "./components/autentificare/ManagementStockCard";
import DraftProductCard from "./components/autentificare/DraftProductCard";
import { FisaProdusCard } from "./components/autentificare/FisaProdusCard";
import InvestitiiFinantareCard from "./components/autentificare/InvestitiiFinantareCard";
import StudioProgramareCard from "./components/autentificare/StudioProgramareCard";
import buildHtmlContent from "./HtmlContentBuilder";
import { buildApiUrl } from "./apiConfig";
import { buildStatusApiUrl } from "./statusApiConfig";
import {
  INVESTITII_PRODUCT_DELETED_EVENT,
  normalizeInvestitiiSkuKey,
} from "./investitiiEvents";

const WOO_API_BASE = "https://www.luxuraelite.ro/wp-json/wc/v3";
const WOO_PROXY_BASE = "https://server-9p4hx.kinsta.app/api/tasks/woo";
const AI_REPHRASE_URL = "https://server-9p4hx.kinsta.app/api/qc/openai-rephrase";
const WOO_CONSUMER_KEY = "ck_a941824be557253d3765a1f6242e6d6a590da999";
const WOO_CONSUMER_SECRET = "cs_6b0d98e4c442e707be9888cafff1fac17cdbf259";
const WOO_LOCATION_META_KEY = "_luxura_location";
const WOO_ATTRIBUTE_CONFIG = {
  condition: { id: 9, name: "Condiție" },
  designers: { id: 10, name: "Designeri" },
  badge: { id: 15, name: "Badge" },
  color: { id: 2, name: "Culoare" },
  category: { id: 11, name: "Categorie" },
  subcategory: { id: 12, name: "Subcategorie" },
  designerId: { id: 24, name: "DesignerID" },
  apparelSize: { id: 16, name: "Marime haine" },
  footwearSize: { id: 8, name: "Marime incaltaminte" },
  dimension: { id: 1, name: "Dimensiune" },
  included: { id: 23, name: "Insotit" },
  entrupy: { id: 22, name: "Entrupy" },
  targetAudience: { id: 17, name: "Pentru" },
};
const SUMO_PLAN_MAP = {
  "1 Rata": ["10956"],
  "2 Rate": ["10956", "10957"],
  "3 Rate": ["10956", "10957", "10969"],
  "1 Rata Speciala": ["106724"],
};
const CONDITION_VALUE_MAP = {
  Nou: 1,
  Excelent: 2,
  "Foarte Buna": 3,
  "Foarte Bună": 3,
  "Foarte Bun": 3,
  Buna: 4,
  Bună: 4,
  Bun: 4,
  Rezonabil: 5,
};

const DESCRIERE_TOGGLE_REGEX =
  /(\[vc_toggle\s+title=("|')DESCRIERE\2[\s\S]*?\])([\s\S]*?)(\s*\[\/vc_toggle\])/i;
const JIRA_IMAGE_FILENAME_REGEX = /\.(jpe?g|png|gif|webp|heic|heif)$/i;
const JIRA_MASTER_FILENAME_REGEX = /(^|[_\-\s])master([_\-\s.]|$)/i;

const isJiraImageAttachment = (attachment = {}) => {
  const filename = String(attachment.filename || attachment.name || "");
  const mimeType = String(
    attachment.mimeType || attachment.mimetype || attachment.contentType || "",
  ).toLowerCase();

  return mimeType.startsWith("image/") || JIRA_IMAGE_FILENAME_REGEX.test(filename);
};

const selectMainJiraImageAttachment = (attachments = [], preferredAttachmentId = "") => {
  const imageAttachments = attachments.filter(isJiraImageAttachment);
  const preferredImage = imageAttachments.find(
    (attachment) =>
      preferredAttachmentId &&
      String(attachment?.id || "") === String(preferredAttachmentId)
  );

  if (preferredImage) {
    return preferredImage;
  }

  return (
    imageAttachments.find((attachment) =>
      JIRA_MASTER_FILENAME_REGEX.test(
        String(attachment.filename || attachment.name || ""),
      ),
    ) ||
    imageAttachments.find((attachment) =>
      String(attachment.filename || attachment.name || "")
        .toLowerCase()
        .includes("master"),
    ) ||
    imageAttachments[0] ||
    null
  );
};

const extensionFromMimeType = (mimeType = "") => {
  const normalized = String(mimeType || "").toLowerCase();
  if (normalized.includes("png")) return "png";
  if (normalized.includes("webp")) return "webp";
  if (normalized.includes("gif")) return "gif";
  if (normalized.includes("heic")) return "heic";
  if (normalized.includes("heif")) return "heif";
  return "jpg";
};

const filenameForJiraImage = (attachment = {}, mimeType = "image/jpeg") => {
  const filename = String(attachment.filename || attachment.name || "").trim();
  if (filename) return filename;
  return `jira-featured.${extensionFromMimeType(mimeType)}`;
};

const replaceDescriereContent = (html, newContent) => {
  if (!html) return "";
  const safeContent = (newContent || "").trim();
  if (!safeContent) return html;
  if (DESCRIERE_TOGGLE_REGEX.test(html)) {
    return html.replace(
      DESCRIERE_TOGGLE_REGEX,
      (_match, openTag, _quote, _inner, closeTag) =>
        `${openTag}\n${safeContent}\n${closeTag}`,
    );
  }
  return html;
};

const buildLayawayMeta = (planKey) => {
  const baseMeta = [
    { key: "_sumo_pp_enable_sumopaymentplans", value: "yes" },
    { key: "_sumo_pp_payment_type", value: "payment-plans" },
    { key: "_sumo_pp_available_dates_from", value: "" },
    { key: "_sumo_pp_available_dates_to", value: "" },
    { key: "_sumo_pp_apply_global_settings", value: "" },
    { key: "_sumo_pp_force_deposit", value: "" },
    { key: "_sumo_pp_default_selected_type", value: "payment-plans" },
    { key: "_sumo_pp_deposit_type", value: "pre-defined" },
    { key: "_sumo_pp_deposit_price_type", value: "fixed-price" },
    { key: "_sumo_pp_pay_balance_type", value: "after" },
    { key: "_sumo_pp_pay_balance_after", value: "" },
    { key: "_sumo_pp_pay_balance_before", value: "" },
    { key: "_sumo_pp_pay_balance_before_booked_date", value: "" },
    { key: "_sumo_pp_set_expired_deposit_payment_as", value: "normal" },
    { key: "_sumo_pp_fixed_deposit_price", value: "" },
    { key: "_sumo_pp_fixed_deposit_percent", value: "" },
    {
      key: "_sumo_pp_user_defined_deposit_type",
      value: "percent-of-product-price",
    },
    { key: "_sumo_pp_min_user_defined_deposit_price", value: "" },
    { key: "_sumo_pp_max_user_defined_deposit_price", value: "" },
    { key: "_sumo_pp_min_deposit", value: "" },
    { key: "_sumo_pp_max_deposit", value: "" },
  ];

  const ids = SUMO_PLAN_MAP[planKey] || [];
  const selected = {
    key: "_sumo_pp_selected_plans",
    value: { col_1: ids },
  };
  return [...baseMeta, selected];
};

const syncLayawayPlan = async ({ productId, enabled, plan }) => {
  try {
    let metaPayload = [];
    if (!enabled) {
      metaPayload = [
        { key: "_sumo_pp_enable_sumopaymentplans", value: "no" },
        { key: "_sumo_pp_selected_plans", value: { col_1: [] } },
      ];
    } else if (plan && SUMO_PLAN_MAP[plan]) {
      metaPayload = buildLayawayMeta(plan);
    } else {
      console.warn(
        "[WooSync] Layaway activat fără plan valid, ignorăm actualizarea SUMO.",
      );
      return false;
    }

    await axios.post(`${WOO_PROXY_BASE}/product/${productId}`, {
      meta_data: metaPayload,
    });
    return true;
  } catch (error) {
    console.error(
      "[WooSync] Eroare la actualizarea SUMO Payment Plans:",
      error?.response?.data || error.message,
    );
    return false;
  }
};

const deriveLayawayStateFromMeta = (metaData = []) => {
  const enableEntry = metaData.find(
    (item) => item?.key === "_sumo_pp_enable_sumopaymentplans",
  );
  const selectedEntry = metaData.find(
    (item) => item?.key === "_sumo_pp_selected_plans",
  );
  const enabled = enableEntry?.value === "yes";
  const selectedIds = Array.isArray(selectedEntry?.value?.col_1)
    ? selectedEntry.value.col_1
    : [];
  const planKey = Object.keys(SUMO_PLAN_MAP).find((key) => {
    const ids = SUMO_PLAN_MAP[key];
    if (ids.length !== selectedIds.length) {
      return false;
    }
    return ids.every((id, idx) => id === selectedIds[idx]);
  });
  return { enabled, plan: planKey || "" };
};

const extractDescriereBlock = (html = "") => {
  if (!html) return "";
  const match = DESCRIERE_TOGGLE_REGEX.exec(html);
  if (match) {
    return (match[3] || "").trim();
  }
  return html.trim();
};

const normalizeSimple = (value = "") =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const normalizedMatchesToken = (normalizedSource, token) => {
  if (!normalizedSource) {
    return false;
  }
  const normalizedTarget = normalizeSimple(token);
  if (!normalizedTarget) {
    return false;
  }
  return (
    normalizedSource === normalizedTarget ||
    normalizedSource.includes(normalizedTarget)
  );
};

const buildWooDetailMap = (htmlString = "") => {
  const detailMap = {};
  if (!htmlString || typeof window === "undefined" || !window.DOMParser) {
    return detailMap;
  }
  try {
    const parser = new window.DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");
    const groups = doc.querySelectorAll(".pdp__section-group");
    groups.forEach((group) => {
      const label = group
        .querySelector(".pdp__group-title")
        ?.textContent?.trim();
      const valueNode = group.querySelector(".pdp__group-item");
      if (!label || !valueNode) return;
      const combined = valueNode.textContent
        ? valueNode.textContent
            .split("\n")
            .map((part) => part.replace(/\s+/g, " ").trim())
            .filter(Boolean)
            .join("\n")
        : "";
      if (combined) {
        detailMap[label] = combined;
      }
    });
    const dimensionLabelMap = {
      marime: "Mărime",
      bust: "Bust",
      maneca: "Mânecă",
      umar: "Umăr",
      lungime: "Lungime",
      inaltime: "Înălțime",
      adancime: "Adâncime",
      latime: "Lățime",
      circumferinta: "Circumferință",
      "observatii marime": "Observații mărime",
    };
    const dimensionRegex =
      /(M[ăa]rime|Observații mărime|Observatii marime|Bust|M[âa]nec[ăa]|Um[ăa]r|Lungime|Înălțime|Inaltime|Adâncime|Adancime|Lățime|Latime|Circumferință|Circumferinta)\s*:\s*([^<\n]+)/gi;
    let match;
    while ((match = dimensionRegex.exec(htmlString))) {
      const rawLabel = (match[1] || "").trim();
      const normalizedKey = rawLabel
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
      const canonicalLabel = dimensionLabelMap[normalizedKey] || rawLabel;
      const value = (match[2] || "").trim();
      if (value) {
        detailMap[canonicalLabel] = value;
      }
    }
  } catch (error) {
    console.error("Failed to parse WooCommerce description:", error);
  }
  return detailMap;
};

const normalizeDimensionLabel = (label = "") =>
  String(label || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const parseDimensionSegments = (raw = "") => {
  const result = {};
  if (!raw) {
    return result;
  }
  const cleaned = String(raw || "")
    .replace(/\|\|/g, "/")
    .replace(/\r/g, "\n")
    .replace(/\n/g, "/");
  const segments = cleaned
    .split("/")
    .map((segment) => segment.replace(/\s+/g, " ").trim())
    .filter(Boolean);

  segments.forEach((segment, index) => {
    if (!segment) return;
    const colonIndex = segment.indexOf(":");
    if (colonIndex === -1) {
      if (!result.size) {
        result.size = segment.trim();
      } else if (!result.fallback) {
        result.fallback = segment.trim();
      }
      return;
    }
    const label = segment.slice(0, colonIndex);
    const value = segment.slice(colonIndex + 1).trim();
    if (!value) return;
    const normalized = normalizeDimensionLabel(label);
    switch (normalized) {
      case "marime":
      case "marime dimensiune":
      case "size":
        result.size = value;
        break;
      case "bust":
        result.bust = value;
        break;
      case "maneca":
      case "manecă":
      case "mâneca":
      case "mânecă":
        result.sleeve = value;
        break;
      case "umar":
      case "umăr":
        result.shoulder = value;
        break;
      case "lungime":
        result.length = value;
        break;
      case "inaltime":
      case "inalt":
      case "înaltime":
      case "înălțime":
        result.height = value;
        break;
      case "adancime":
      case "adâncime":
        result.depth = value;
        break;
      case "latime":
      case "lățime":
      case "lat":
        result.width = value;
        break;
      case "circumferinta":
      case "circumferință":
        result.circumference = value;
        break;
      case "observatiimarime":
      case "observatii marime":
      case "observatiimarimi":
      case "observatii marimi":
      case "observatiimărime":
      case "observații mărime":
        result.sizeObservation = value;
        break;
      default:
        if (!result.extra) {
          result.extra = {};
        }
        result.extra[normalized] = value;
        break;
    }
  });

  return result;
};

const getWooAttributeOptions = (product, attributeName) => {
  const lowerName = (attributeName || "").toLowerCase();
  const attr =
    product?.attributes?.find((item) => {
      const attrName = (item.name || "").toLowerCase();
      const attrSlug = (item.slug || "").toLowerCase();
      return attrName === lowerName || attrSlug === lowerName;
    }) || null;
  return Array.isArray(attr?.options) ? attr.options : [];
};

const ExpandMore = styled((props) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? "rotate(0deg)" : "rotate(180deg)",
  marginLeft: "auto",
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.shortest,
  }),
}));

const modalStyle = {
  position: "fixed",
  top: "50%",
  left: "50%",
  width: "80%",
  height: "80%",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  overflowY: "auto",
  transform: "translate(-50%, -50%)",
  display: "flex",
  flexDirection: "column",
};

const auroraAnimation = keyframes`
  0% { box-shadow: 0 0 10px #1E90FF, 0 0 20px #1E90FF, 0 0 30px #1E90FF; }
  50% { box-shadow: 0 0 20px #00FFAB, 0 0 30px #00FFAB, 0 0 40px #00FFAB; }
  100% { box-shadow: 0 0 10px #1E90FF, 0 0 20px #1E90FF, 0 0 30px #1E90FF; }
`;

// Stil pentru iconița butonului cu animația aplicată
const AnimatedIconButton = styled(IconButton)(({ theme }) => ({
  color: "#1E90FF",
  "&:hover": {
    animation: `${auroraAnimation} 2s infinite`,
  },
}));

function extractTitle(htmlString) {
  const tmp = document.createElement("div");
  tmp.innerHTML = htmlString;
  return tmp.textContent || "";
}

const cleanSummaryForBrandModel = (value = "") =>
  String(value || "")
    .trim()
    .replace(/^[\s#-]*\d+[\s#-]*/, "")
    .trim();

const normalizeAiField = (value) => {
  if (typeof value !== "string") {
    return "";
  }
  const trimmed = value.trim();
  if (!trimmed || /^n\/?a$/i.test(trimmed) || /^unknown$/i.test(trimmed)) {
    return "";
  }
  return trimmed;
};

const normalizeCategoryDisplay = (...values) => {
  for (const value of values) {
    const rawValue =
      typeof value === "object" && value !== null
        ? value.label || value.name || value.value || ""
        : value;
    const text = String(rawValue || "").trim();
    if (text && !/^\d+$/.test(text)) {
      return text;
    }
  }
  return "";
};

const parseAiJsonObject = (value = "") => {
  const content = String(value || "")
    .replace(/^```(?:json)?/i, "")
    .replace(/```$/i, "")
    .trim();

  try {
    return JSON.parse(content);
  } catch (_error) {
    const jsonMatch = content.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      return null;
    }
    try {
      return JSON.parse(jsonMatch[0]);
    } catch (_nestedError) {
      return null;
    }
  }
};

const fallbackBrandModelFromSummary = (summary = "") => {
  const cleanSummary = cleanSummaryForBrandModel(summary);
  const [brand = "", ...modelParts] = cleanSummary.split(/\s+/).filter(Boolean);
  return {
    brand,
    model: modelParts.join(" "),
  };
};

const normalizeStockSku = (value = "") =>
  String(value || "")
    .replace(/\s+/g, "")
    .trim()
    .toUpperCase();

const normalizeFieldText = (value = "") => {
  if (value && typeof value === "object") {
    return String(value.value || value.label || value.name || "").trim();
  }
  return String(value || "").trim();
};

const parseRonAmount = (value = "") => {
  const raw = normalizeFieldText(value)
    .replace(/\s+/g, "")
    .replace(/[^\d,.-]/g, "");
  if (!raw) return 0;

  const normalized =
    raw.includes(",") && raw.includes(".")
      ? raw.lastIndexOf(",") > raw.lastIndexOf(".")
        ? raw.replace(/\./g, "").replace(",", ".")
        : raw.replace(/,/g, "")
      : raw.replace(",", ".");
  const number = Number(normalized);
  return Number.isFinite(number) ? Math.max(0, number) : 0;
};

export default function AutentificareStep({
  issueId,
  customFieldValue,
  onUpdateCustomField,
  contactName,
  contactPhone,
  contactEmail,
  category,
  categoryLabel,
  summary,
  nrContract,
  skuJira,
  selectedAddress,
  vendorDataOverride,
  mainImageAttachmentId = "",
}) {
  const [expanded, setExpanded] = useState(false);
  const brandModelRequestRef = useRef(0);
  const [condition, setCondition] = useState("");
  const [description, setDescription] = useState("");
  const [qcStatus, setQcStatus] = useState(customFieldValue || "");
  const qcStatusRef = useRef(qcStatus);
  const [priceOption, setPriceOption] = useState("");
  const [currentPrice, setCurrentPrice] = useState("");
  const [newPrice, setNewPrice] = useState("");
  const [acceptedRenegotiationPrice, setAcceptedRenegotiationPrice] =
    useState("");
  const [isAcceptingRenegotiationPrice, setIsAcceptingRenegotiationPrice] =
    useState(false);
  const [isCreatingAdditionalAct, setIsCreatingAdditionalAct] = useState(false);
  const [additionalActFeedback, setAdditionalActFeedback] = useState(null);
  const showRenegotiation = priceOption?.trim() === "Propunere pret nou";
  const [anchorEl, setAnchorEl] = useState(null);
  const [openReportModal, setOpenReportModal] = useState(false);
  const [awbData, setAwbData] = useState({
    street: "",
    number: "",
    postalCode: "",
    city: "",
    county: "",
    country: "Romania",
    observations: "",
    contactName: contactName || "",
    contactPhone: contactPhone || "",
    contactEmail: contactEmail || "",
    pickupDate: "",
    scheduleDate: "",
  });

  // Populate AWB fields from Vendor Information selected address
  useEffect(() => {
    if (selectedAddress) {
      setAwbData((prev) => ({
        ...prev,
        street: selectedAddress.address_line1 || prev.street || "",
        number: selectedAddress.address_line2 || prev.number || "",
        postalCode: selectedAddress.postal_code || prev.postalCode || "",
        city: selectedAddress.city || prev.city || "",
        county: selectedAddress.state || prev.county || "",
        country: selectedAddress.country || prev.country || "Romania",
      }));
    }
  }, [selectedAddress]);
  const [rejectionReason, setRejectionReason] = useState("");
  const [sesiuniDisponibile, setSesiuniDisponibile] = useState([]);
  const [selectedSesiune, setSelectedSesiune] = useState("");
  const [linkedIssues, setLinkedIssues] = useState([]);
  const [fisaProdusId, setFisaProdusId] = useState("");
  const [fisaProdusIdStatus, setFisaProdusIdStatus] = useState("");
  const [fisaProdusStatus, setFisaProdusStatus] = useState("");
  const [ciornaPlatforma, setCiornaPlatforma] = useState("Not available");
  const [wooCommerceTitle, setWooCommerceTitle] = useState("");
  const [wooCommerceStatus, setWooCommerceStatus] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [showAnalysis, setShowAnalysis] = useState(false); // Stare pentru a controla vizibilitatea analizei
  const [manualAvgPrice, setManualAvgPrice] = useState(""); // Stare pentru Prețul de Vânzare
  const [error, setError] = useState("");
  const [rephraseError, setRephraseError] = useState("");
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [filteredProductsLoading, setFilteredProductsLoading] = useState(false);
  const [filteredProductsError, setFilteredProductsError] = useState("");
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [isCreatingDraft, setIsCreatingDraft] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]); // Selectarea categoriei
  const [nextProductId, setNextProductId] = useState(null); // Următorul ID de produs
  const [showCreateProduct, setShowCreateProduct] = useState(false);
  const [productData, setProductData] = useState(null);
  const [availableBadges, setAvailableBadges] = useState([]);
  const [selectedBadges, setSelectedBadges] = useState([]);
  const [jiraFields, setJiraFields] = useState(null);
  const additionalActPreview = useAdditionalActPreview({
    issueId,
    contract: jiraFields?.Contract,
    enabled: showRenegotiation,
    acceptedPrice: acceptedRenegotiationPrice,
    currentPrice,
    newPrice,
  });
  const [vendorData, setVendorData] = useState(null);
  const currentVendorEmail = String(jiraFields?.email || "")
    .trim()
    .toLowerCase();
  const overrideVendorEmail = String(vendorDataOverride?.vendor?.email || "")
    .trim()
    .toLowerCase();
  const fetchedVendorEmail = String(vendorData?.vendor?.email || "")
    .trim()
    .toLowerCase();
  const overrideMatchesCurrentVendor = Boolean(
    currentVendorEmail && overrideVendorEmail === currentVendorEmail,
  );
  const fetchedVendorMatchesCurrentVendor = Boolean(
    currentVendorEmail && fetchedVendorEmail === currentVendorEmail,
  );
  const effectiveVendorData = overrideMatchesCurrentVendor
    ? vendorDataOverride
    : fetchedVendorMatchesCurrentVendor
      ? vendorData
      : null;
  const [tipContract, setTipContract] = useState("");
  const [syncToPlatform, setSyncToPlatform] = useState(true);
  const [customFieldValues, setCustomFieldValues] = useState({
    customfield_10066: "",
    customfield_10083: "",
    customfield_10077: "",
    customfield_10072: "",
    customfield_10073: "",
    customfield_10080: "",
    customfield_10183: "",
    customfield_10173: "",
    customfield_10078: "",
    customfield_10165: "",
    customfield_10185: "",
    customfield_10186: "",
    customfield_10107: "",
  });
  const [customFieldOptions, setCustomFieldOptions] = useState({
    customfield_10080: [],
    customfield_10183: [],
    customfield_10173: [],
    customfield_10165: [],
    customfield_10185: [],
  });

  useEffect(() => {
    if (overrideMatchesCurrentVendor) {
      setVendorData(vendorDataOverride);
    }
  }, [overrideMatchesCurrentVendor, vendorDataOverride]);

  useEffect(() => {
    setAcceptedRenegotiationPrice("");
    setAdditionalActFeedback(null);
    setVendorData(null);
  }, [issueId]);
  const initialProductDetails = {
    trimisLaPozat: "",
    culoareHW: "",
    categorieProd: "",
    subcategorie: "",
    brand: "",
    marimeDimensiune: "",
    bustDimensiune: "",
    sleeveDimensiune: "",
    shoulderDimensiune: "",
    culoareExterioara: "",
    culoareInterioara: "",
    materialExterior: "",
    materialInterior: "",
    designerId: "",
    entrupyId: "",
    model: "",
    pentru: "",
    lungime: "",
    inaltime: "",
    adancime: "",
    layawayEnabled: false,
    layawayOption: "",
    location: "",
    msrpPrice: "",
    wooDescription: "",
    wooCategoriesSelected: [],
    existingCategoryAttributes: [],
    existingSubcategoryAttributes: [],
  };
  const [productDetails, setProductDetails] = useState(initialProductDetails);
  const productDetailsRef = useRef(initialProductDetails);
  const [productDetailOptions, setProductDetailOptions] = useState({
    trimisLaPozat: [],
    categorieProd: [],
  });
  const [wooProductSnapshot, setWooProductSnapshot] = useState(null);
  const [isWooDetailsLoading, setIsWooDetailsLoading] = useState(false);
  const [wooReplayToken, setWooReplayToken] = useState(0);
  const [jiraProductSnapshot, setJiraProductSnapshot] = useState(
    initialProductDetails,
  );

  const getCiornaSkuForStock = useCallback(() => {
    const rawSku =
      customFieldValues.customfield_10083?.value ||
      customFieldValues.customfield_10083 ||
      skuJira ||
      (ciornaPlatforma && ciornaPlatforma !== "Not available"
        ? ciornaPlatforma
        : "");
    return String(rawSku || "").trim();
  }, [ciornaPlatforma, customFieldValues.customfield_10083, skuJira]);
  const effectiveCiornaSku = getCiornaSkuForStock();
  const [hasLoadedJiraSnapshot, setHasLoadedJiraSnapshot] = useState(false);
  const [hasLoadedWooSnapshot, setHasLoadedWooSnapshot] = useState(false);
  const [isInitialFisaLoading, setIsInitialFisaLoading] = useState(true);
  const [wooSyncStatus, setWooSyncStatus] = useState({
    message: "",
    severity: "success",
  });
  const [investitiiEnabled, setInvestitiiEnabled] = useState(false);
  const [investitiiPools, setInvestitiiPools] = useState([]);
  const [investitiiPoolId, setInvestitiiPoolId] = useState("");
  const [investitiiPoolsLoading, setInvestitiiPoolsLoading] = useState(false);
  const [investitiiSaving, setInvestitiiSaving] = useState(false);
  const [investitiiMessage, setInvestitiiMessage] = useState(null);
  const [investitiiAssignedProduct, setInvestitiiAssignedProduct] = useState(null);
  const [investitiiAssignmentLoading, setInvestitiiAssignmentLoading] =
    useState(false);
  const enableWooFetchInParent = false;

  useEffect(() => {
    productDetailsRef.current = productDetails;
  }, [productDetails]);

  const applyWooProductDetailsFromWoo = useCallback(
    (wooProduct) => {
      if (!wooProduct) return;
      const stripHtml = (value = "") => {
        if (!value && value !== 0) return "";
        return String(value || "")
          .replace(/<br\s*\/?>/gi, "||")
          .replace(/\r?\n+/g, "||")
          .replace(/<[^>]+>/g, " ")
          .replace(/\s+/g, " ")
          .trim();
      };
      const stripLabelPrefixes = (text = "", prefixes = []) => {
        if (!text) return "";
        const trimmed = text.trim();
        const lower = trimmed.toLowerCase();
        for (const prefix of prefixes) {
          const lowerPrefix = prefix.toLowerCase();
          if (lower.startsWith(lowerPrefix)) {
            return trimmed
              .slice(prefix.length)
              .replace(/^[:\s-]+/, "")
              .trim();
          }
        }
        return trimmed;
      };
      const sanitizeObservationValue = (value = "") =>
        stripLabelPrefixes(stripHtml(value), [
          "Observații mărime",
          "Observatii marime",
          "Observații marimi",
          "Observatii marimi",
        ]);
      const sanitizeSizeValue = (value = "") =>
        stripLabelPrefixes(stripHtml(value), [
          "Mărime",
          "Marime",
          "Size",
          "Mărime încălțăminte",
          "Marime incaltaminte",
        ])
          .replace(/\s*cm$/i, "")
          .trim();
      const attrValue = (name) => {
        const options = getWooAttributeOptions(wooProduct, name);
        return Array.isArray(options) && options.length ? options[0] : "";
      };
      const attrValueAny = (names = []) => {
        for (const name of names) {
          const value = attrValue(name);
          if (value) return value;
        }
        return "";
      };
      const attrValueLoose = (tokens = []) => {
        const normalizedTokens = tokens
          .map((token) => normalizeKey(token))
          .filter(Boolean);
        if (!normalizedTokens.length) return "";
        const attributes = Array.isArray(wooProduct?.attributes)
          ? wooProduct.attributes
          : [];
        for (const attr of attributes) {
          const attrName = normalizeKey(attr?.name || "");
          const attrSlug = normalizeKey(attr?.slug || "");
          const matches = normalizedTokens.some(
            (token) => attrName.includes(token) || attrSlug.includes(token),
          );
          if (matches) {
            const options = Array.isArray(attr?.options) ? attr.options : [];
            const first = options.find((opt) => String(opt || "").trim());
            if (first) {
              return first;
            }
          }
        }
        return "";
      };
      const attrOptions = (name) => {
        const options = getWooAttributeOptions(wooProduct, name);
        return Array.isArray(options) ? [...options] : [];
      };
      const detailMap = buildWooDetailMap(wooProduct.description || "");
      const shortDetailMap = buildWooDetailMap(
        wooProduct.short_description || "",
      );
      const mergedDetailMap = { ...shortDetailMap, ...detailMap };
      const normalizeKey = (value = "") =>
        String(value || "")
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "")
          .trim();
      const detailMapNormalized = Object.entries(mergedDetailMap).reduce(
        (acc, [key, value]) => {
          const normalized = normalizeKey(key);
          if (normalized && !acc[normalized]) {
            acc[normalized] = value;
          }
          return acc;
        },
        {},
      );
      const detailValue = (...labels) => {
        for (const key of labels) {
          if (key && mergedDetailMap[key]) {
            return mergedDetailMap[key];
          }
        }
        return "";
      };
      const detailValueLoose = (...labels) => {
        for (const key of labels) {
          const normalized = normalizeKey(key);
          if (normalized && detailMapNormalized[normalized]) {
            return detailMapNormalized[normalized];
          }
        }
        return "";
      };
      const extractLabeledValueFromHtml = (html = "", labels = []) => {
        if (!html) return "";
        const normalizedLabels = labels
          .map((label) => normalizeKey(label))
          .filter(Boolean);
        if (!normalizedLabels.length) return "";
        const text = String(html || "")
          .replace(/<br\s*\/?>/gi, "\n")
          .replace(/<\/p>/gi, "\n")
          .replace(/<[^>]+>/g, " ")
          .replace(/\u00a0/g, " ")
          .replace(/\s+/g, " ")
          .trim();
        const lines = text
          .split("\n")
          .map((line) => line.trim())
          .filter(Boolean);
        for (const line of lines) {
          const normalizedLine = normalizeKey(line);
          for (const label of normalizedLabels) {
            if (normalizedLine.startsWith(label)) {
              const raw = line.slice(label.length).replace(/^[:\s-]+/, "");
              return raw.trim();
            }
          }
        }
        return "";
      };
      const normalizeListValue = (value) => {
        if (!value) {
          return "";
        }
        const base = String(value || "")
          .replace(/\|\|/g, "\n")
          .replace(/\r/g, "\n");
        const tokens = base
          .split(/[\n,;]+/)
          .map((token) => token.replace(/\s+/g, " ").trim())
          .filter(Boolean);
        return tokens.length ? tokens.join("\n") : base.trim();
      };
      const pickFirstListValue = (value) => {
        const normalized = normalizeListValue(value);
        if (!normalized) return "";
        const [first] = normalized.split("\n");
        return (first || "").trim();
      };
      const metaData = wooProduct.meta_data || [];
      const findMetaValue = (keys = []) => {
        const normalizedKeys = keys.map((key) => normalizeKey(key));
        const match = metaData.find((meta) =>
          normalizedKeys.includes(normalizeKey(meta?.key || "")),
        );
        return match?.value ?? "";
      };
      const findMetaValueLoose = (tokens = []) => {
        const normalizedTokens = tokens
          .map((token) => normalizeKey(token))
          .filter(Boolean);
        if (!normalizedTokens.length) return "";
        const match = metaData.find((meta) => {
          const metaKey = normalizeKey(meta?.key || "");
          return normalizedTokens.some((token) => metaKey.includes(token));
        });
        return match?.value ?? "";
      };
      const layawayState = deriveLayawayStateFromMeta(metaData);
      const metaLocationEntry = metaData.find(
        (meta) =>
          (meta?.key || "").toLowerCase() ===
          WOO_LOCATION_META_KEY.toLowerCase(),
      );
      const locationFromMeta = metaLocationEntry?.value || "";
      const locationFromCategories = (wooProduct.categories || []).some((cat) =>
        (cat?.name || "").toLowerCase().includes("boutique"),
      )
        ? "Luxura Boutique"
        : "";
      const msrpFromMeta =
        findMetaValue([
          "msrp_price",
          "_msrp_price",
          "msrp",
          "retail_price",
          "pret_retail",
          "pret_retail_ron",
        ]) || findMetaValueLoose(["msrp", "retail", "pretretail"]);
      const msrpFromProductField =
        wooProduct?.msrp_price ||
        wooProduct?.df_msrp_price ||
        wooProduct?.msrp ||
        "";
      const attributeCategories = attrOptions("categorie");
      const attributeSubcategories = attrOptions("subcategorie");
      const wooCategoriesRaw = Array.isArray(wooProduct.categories)
        ? wooProduct.categories
        : [];
      const normalizeCategoryToken = (value = "") =>
        normalizeSimple(value).replace(/[^a-z0-9]+/g, "");
      const normalizedWooCategoryNames = wooCategoriesRaw
        .map((cat) => ({
          normalized: normalizeSimple(cat?.name || ""),
          compact: normalizeCategoryToken(cat?.name || ""),
        }))
        .filter((entry) => entry.normalized);
      const designerBagMarkersRaw = [
        "genti de designer pre-owned",
        "genti de designeri pre-owned",
      ];
      const designerBagMarkers = designerBagMarkersRaw.map((marker) =>
        normalizeSimple(marker),
      );
      const designerBagCompactMarkers = designerBagMarkersRaw.map((marker) =>
        normalizeCategoryToken(marker),
      );
      const hasDesignerBagCategory = normalizedWooCategoryNames.some(
        ({ normalized, compact }) =>
          designerBagMarkers.some((marker) => normalized.includes(marker)) ||
          designerBagCompactMarkers.some((marker) => compact.includes(marker)),
      );

      const updates = {};
      const maybeAssign = (key, value) => {
        if (
          value !== undefined &&
          value !== null &&
          String(value).trim() !== ""
        ) {
          updates[key] = value;
        }
      };
      updates.existingCategoryAttributes = attributeCategories;
      updates.existingSubcategoryAttributes = attributeSubcategories;

      let categorieValue =
        detailValue("Categorie", "Categorie Prod") ||
        attrValue("categorie") ||
        wooProduct.categories?.[0]?.name ||
        "";
      if (hasDesignerBagCategory) {
        categorieValue = "Genti";
      }
      maybeAssign("categorieProd", categorieValue);
      const normalizedCategorieValue = normalizeSimple(categorieValue || "");
      const isFootwearCategory =
        normalizedCategorieValue.includes("incaltaminte");

      maybeAssign(
        "subcategorie",
        detailValue("Subcategorie") || attrValue("subcategorie"),
      );
      maybeAssign("pentru", detailValue("Pentru") || attrValue("pentru"));
      const primarySizeValueRaw = detailValue("Mărime", "Marime");
      const observationsSizeValueRaw = detailValue(
        "Observații mărime",
        "Observatii marime",
      );
      const dimensionRawSource =
        primarySizeValueRaw ||
        observationsSizeValueRaw ||
        wooProduct.description ||
        wooProduct.short_description ||
        "";
      const parsedSegments = parseDimensionSegments(
        stripHtml(dimensionRawSource),
      );
      const observationFromSegments =
        parsedSegments.sizeObservation ||
        parsedSegments.extra?.observatiimarime ||
        parsedSegments.extra?.observatiimarimi ||
        "";
      const normalizedObservationSize = sanitizeObservationValue(
        observationsSizeValueRaw || observationFromSegments,
      );
      const resolvedSizeValueRaw =
        parsedSegments.size || pickFirstListValue(primarySizeValueRaw);
      const normalizedResolvedSize = sanitizeSizeValue(
        resolvedSizeValueRaw || primarySizeValueRaw,
      );
      if (normalizedObservationSize) {
        maybeAssign("marimeDimensiune", normalizedObservationSize);
      }
      const bustValue = detailValue("Bust") || parsedSegments.bust;
      const sleeveValue =
        detailValue("Mânecă", "Maneca") || parsedSegments.sleeve;
      const shoulderValue =
        detailValue("Umăr", "Umar") || parsedSegments.shoulder;
      if (bustValue) {
        maybeAssign("bustDimensiune", bustValue);
      }
      if (sleeveValue) {
        maybeAssign("sleeveDimensiune", sleeveValue);
      }
      if (shoulderValue) {
        maybeAssign("shoulderDimensiune", shoulderValue);
      }

      const colorExt = detailValue("Culoare Exterioară", "Culoare") || "";
      maybeAssign("culoareExterioara", colorExt);
      maybeAssign(
        "culoareInterioara",
        detailValue("Culoare Interioară", "Culoare interior"),
      );

      const materialExt = normalizeListValue(
        detailValue("Material Exterior", "Material"),
      );
      const materialInt = normalizeListValue(detailValue("Material Interior"));
      maybeAssign("materialExterior", materialExt);
      maybeAssign("materialInterior", materialInt);

      const hardwareColor =
        detailValueLoose(
          "Culoare Hardware",
          "Culoare HW",
          "Hardware",
          "Hardware Color",
          "Culoare metal",
        ) ||
        attrValueAny([
          "culoare hardware",
          "culoare hw",
          "hardware",
          "hardware color",
          "culoare metal",
        ]);
      maybeAssign("culoareHW", hardwareColor);
      maybeAssign(
        "designerId",
        detailValue("Designer ID") || attrValue("designerid"),
      );
      maybeAssign(
        "entrupyId",
        detailValue("Entrupy ID") || attrValue("entrupy"),
      );

      const resolvedLocation =
        detailValue("Locație") || locationFromMeta || locationFromCategories;
      maybeAssign("location", resolvedLocation);

      const dims = wooProduct.dimensions || {};
      const panjang =
        detailValue("Lungime") ||
        parsedSegments.length ||
        parsedSegments.width ||
        parsedSegments.circumference;
      const inaltimeVal =
        detailValue("Înălțime", "Inaltime", "Circumferință") ||
        parsedSegments.height ||
        parsedSegments.circumference;
      const adancimeVal =
        detailValue("Adâncime", "Adancime", "Lățime", "Latime") ||
        parsedSegments.depth ||
        parsedSegments.width;
      const footwearSizeValue = normalizedResolvedSize;
      if (isFootwearCategory && footwearSizeValue) {
        maybeAssign("lungime", footwearSizeValue);
      } else {
        maybeAssign("lungime", panjang || dims.length);
      }
      maybeAssign("inaltime", inaltimeVal || dims.height);
      maybeAssign("adancime", adancimeVal || dims.width);

      if (typeof layawayState.enabled === "boolean") {
        updates.layawayEnabled = layawayState.enabled;
        updates.layawayOption = layawayState.enabled ? layawayState.plan : "";
      }
      const msrpFromDetail =
        detailValueLoose(
          "Pret de retail",
          "Preț de retail",
          "Retail Price",
          "MSRP",
          "Pret retail",
        ) ||
        extractLabeledValueFromHtml(wooProduct.description || "", [
          "Pret de retail",
          "Preț de retail",
          "Retail Price",
          "MSRP",
          "Pret retail",
        ]) ||
        extractLabeledValueFromHtml(wooProduct.short_description || "", [
          "Pret de retail",
          "Preț de retail",
          "Retail Price",
          "MSRP",
          "Pret retail",
        ]) ||
        attrValueAny([
          "msrp",
          "pret retail",
          "pret de retail",
          "retail price",
        ]) ||
        attrValueLoose([
          "msrp",
          "pret retail",
          "pret de retail",
          "retail price",
        ]);
      maybeAssign(
        "msrpPrice",
        msrpFromDetail || msrpFromMeta || msrpFromProductField,
      );
      if (!updates.msrpPrice) {
        console.warn("[Fisa Produs] MSRP missing in Woo payload", {
          sku: wooProduct?.sku,
          id: wooProduct?.id,
          meta_keys: metaData.map((meta) => meta?.key).filter(Boolean),
          attributes: (wooProduct?.attributes || []).map((attr) => ({
            name: attr?.name,
            slug: attr?.slug,
            options: attr?.options,
          })),
          description_snippet: (wooProduct?.description || "").slice(0, 300),
          short_description_snippet: (
            wooProduct?.short_description || ""
          ).slice(0, 300),
        });
      }
      maybeAssign(
        "wooDescription",
        extractDescriereBlock(wooProduct.description || ""),
      );
      const wooCategoriesSelected = Array.isArray(wooProduct.categories)
        ? wooProduct.categories
            .filter(
              (cat) => (cat?.name || "").toLowerCase() !== "fara categorie",
            )
            .map((cat) => ({
              id: cat.id,
              name: cat.name,
              label: cat.name,
              value: cat.id,
              parent: cat.parent ?? null,
              ancestors: [],
            }))
        : [];
      if (wooCategoriesSelected.length) {
        updates.wooCategoriesSelected = wooCategoriesSelected;
      }

      setProductDetails((prev) => ({ ...prev, ...updates }));

      console.log("[Fisa Produs] Woo price/meta snapshot", {
        sku: wooProduct?.sku,
        id: wooProduct?.id,
        price: wooProduct?.price,
        regular_price: wooProduct?.regular_price,
        sale_price: wooProduct?.sale_price,
        meta_keys: metaData.map((meta) => meta?.key).filter(Boolean),
        msrp_meta: msrpFromMeta,
        msrp_detail: msrpFromDetail,
        msrp_final: updates.msrpPrice,
      });
      const resolvedWooPriceRaw =
        wooProduct.price ||
        wooProduct.regular_price ||
        wooProduct.sale_price ||
        findMetaValue([
          "_price",
          "_regular_price",
          "_sale_price",
          "price",
          "regular_price",
          "sale_price",
        ]) ||
        "";
      const resolvedWooPrice = String(resolvedWooPriceRaw || "").trim();
      if (resolvedWooPrice !== "") {
        setCustomFieldValues((prev) => ({
          ...prev,
          customfield_10073: resolvedWooPrice,
        }));
      }
    },
    [setProductDetails, setCustomFieldValues],
  );
  const consumerKey = "ck_a941824be557253d3765a1f6242e6d6a590da999";
  const consumerSecret = "cs_6b0d98e4c442e707be9888cafff1fac17cdbf259";

  const handleFilter = async () => {
    const brandFilter = customFields.brand;
    const categoryFilter = customFields.categorie;
    const modelFilter = customFields.model;

    if (!brandFilter || !categoryFilter) {
      alert("Lipsesc valorile necesare pentru brand sau categorie!");
      return;
    }

    setFilteredProductsLoading(true);
    setFilteredProductsError("");
    setSelectedProductIds([]);
    setProductData(null);

    try {
      const response = await axios.get(
        "https://server-9p4hx.kinsta.app/api/masterdata/filter-products",
        {
          params: {
            brand: brandFilter,
            category: categoryFilter,
            model: modelFilter,
            useAi: "1",
            minMatch: 70,
            limit: 24,
          },
        },
      );
      setFilteredProducts(
        Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.products)
            ? response.data.products
            : [],
      );
    } catch (error) {
      console.error("Error fetching filtered products:", error);
      setFilteredProductsError(
        "Nu am putut căuta produse similare în baza de date.",
      );
    } finally {
      setFilteredProductsLoading(false);
    }
  };

  const fetchTipContract = async () => {
    try {
      const response = await axios.get(`/jira-task-qc/${issueId}/fields`);
      const tipContractValue = response.data.customfield_10183 || "";
      setTipContract(tipContractValue);
      console.log("Fetched Tip_Contract:", tipContractValue);
    } catch (error) {
      console.error("Error fetching Tip_Contract from JIRA:", error);
    }
  };

  // Fetch Tip_Contract when the component mounts or the issueId changes
  useEffect(() => {
    if (issueId) {
      fetchTipContract();
    }
  }, [issueId]);

  useEffect(() => {
    const fetchLastProductId = async () => {
      try {
        const response = await axios.get(
          "https://www.luxuraelite.ro/wp-json/wc/v3/products",
          {
            params: {
              per_page: 1,
              orderby: "id",
              order: "desc",
            },
            headers: {
              Authorization:
                "Basic " + btoa(`${consumerKey}:${consumerSecret}`),
            },
          },
        );
        const lastProductId = response.data[0]?.id || 0;
        setNextProductId(lastProductId + 1);
      } catch (error) {
        console.error("Error fetching last product ID:", error);
      }
    };

    fetchLastProductId();
  }, []);

  // Funcția de selectare a produsului
  const handleProductSelect = async (sku) => {
    const normalizedSku = String(sku || "").trim();
    if (!normalizedSku) {
      console.warn("Produs fără SKU, nu poate fi selectat pentru ciornă.");
      return;
    }

    console.log("Produs selectat:", normalizedSku); // Verifică produsul selectat
    // Extrage ultimele 6 cifre din SKU și setează în customfield_10066
    const numericSuffix = normalizedSku.slice(-6).replace(/\D/g, "");
    console.log("Ultimele 6 cifre extrase:", numericSuffix);
    const selectedProductId = parseInt(numericSuffix, 10);
    console.log("ID-ul extras din SKU:", selectedProductId);

    setCustomFieldValues((prevValues) => ({
      ...prevValues,
      customfield_10066: numericSuffix,
    }));

    setSelectedProductIds([normalizedSku]);

    try {
      const productData = await handleCloneProduct(
        normalizedSku,
        consumerKey,
        consumerSecret,
      );
      console.log("Product Data:", productData); // Afișează datele produsului imediat după selectare
      setProductData(productData);
    } catch (error) {
      console.error("Eroare la clonarea produsului:", error);
    }
  };

  const handleCreareActAditional = async () => {
    if (additionalActFeedback?.preventRetry) return;
    const readiness = getAdditionalActReadiness({
      jiraFields,
      vendorData: effectiveVendorData,
      currentPrice,
      newPrice,
      acceptedPrice: acceptedRenegotiationPrice,
      fallbackPrice: customFieldValues.customfield_10072,
      additionalActPreview,
    });

    if (!readiness.isReady) {
      const missingLabels = readiness.requirements
        .filter((item) => !item.isComplete)
        .map((item) => item.label)
        .join(", ");
      setAdditionalActFeedback({
        severity: "error",
        message: `Actul adițional nu poate fi creat încă. Lipsesc: ${missingLabels}.`,
      });
      return;
    }

    setIsCreatingAdditionalAct(true);
    setAdditionalActFeedback(null);

    try {
      const workflow = getAdditionalActWorkflow(readiness.values.Tip_Contract);
      const requestData = {
        ...(workflow.provider === "zoho"
          ? { issueId, nrAct: readiness.values.nrAct }
          : {}),
        Tip_Contract: readiness.values.Tip_Contract,
        nume: readiness.values.nume,
        telefon: readiness.values.telefon,
        email: readiness.values.email,
        NrContract: readiness.values.NrContract,
        dataIntrare: readiness.values.dataIntrare,
        // The accepted proposal is the source of truth, even before a refetch
        // finishes updating currentPrice in local state.
        pretFinal: readiness.values.pretFinal,
        serieCI: readiness.values.serieCI,
        nrCI: readiness.values.nrCI,
        adresaLinia1: readiness.values.adresaLinia1,
      };

      const response = await axios.post(
        buildApiUrl(workflow.path),
        requestData,
      );

      if (isAdditionalActSubmitted(response, workflow.provider)) {
        setAdditionalActFeedback({
          severity: "success",
          message:
            response.data?.message ||
            "Actul adițional a fost creat și trimis cu succes!",
        });
      } else {
        setAdditionalActFeedback({
          severity: "error",
          message:
            response.data?.message ||
            "Actul adițional nu a fost trimis la semnat. Verifică datele și reîncearcă.",
        });
      }
    } catch (error) {
      console.error("Error creating Act Adițional:", {
        status: error.response?.status,
        message: error.message,
      });
      setAdditionalActFeedback(
        getAdditionalActFailureFeedback(error.response?.data),
      );
    } finally {
      setIsCreatingAdditionalAct(false);
    }
  };

  // Funcție pentru salvarea prețului propus
  const handleSaveNewPrice = async () => {
    const proposedPrice = String(newPrice || "").trim();
    if (!isValidAdditionalActAmount(proposedPrice)) {
      setAdditionalActFeedback({
        severity: "error",
        message: "Introdu o sumă nouă validă, mai mare decât zero.",
      });
      return;
    }

    setIsAcceptingRenegotiationPrice(true);
    setAdditionalActFeedback(null);

    try {
      await axios.put(
        buildApiUrl(`/api/tasks/jira-task/${issueId}`),
        {
          customfield_10072: proposedPrice,
        },
      );
      setAcceptedRenegotiationPrice(proposedPrice);
      setCurrentPrice(proposedPrice);
      setAdditionalActFeedback({
        severity: "success",
        message:
          "Noul preț a fost acceptat și va fi folosit în actul adițional.",
      });
      await fetchJiraFields(); // Re-fetch updated fields after saving new price
      await getRenegotiationQcDetails(); // Re-fetch currentPrice and related fields after update
    } catch (error) {
      console.error("Eroare la salvarea prețului:", error);
      setAdditionalActFeedback({
        severity: "error",
        message:
          error.response?.data?.message ||
          "A apărut o eroare la salvarea prețului.",
      });
    } finally {
      setIsAcceptingRenegotiationPrice(false);
    }
  };

  const handleRejectPrice = async () => {
    const requiredFields = ["street", "number", "postalCode", "city", "county"];
    const missingFields = requiredFields.filter(
      (field) => !String(awbData?.[field] || "").trim(),
    );

    if (missingFields.length > 0) {
      alert(
        "Te rog completează toate câmpurile de adresă (Strada, Nr, Cod poștal, Oraș, Județ) înainte de a genera raportul.",
      );
      return;
    }

    try {
      const renegotiationReason =
        "In urma procesului de evaluare (quality check), pretul de achizitie a fost ajustat conform constatarilor tehnice, insa noua cotatie nu a fost acceptata de client.";

      const payload = {
        ...awbData,
        contactName: awbData.contactName || contactName,
        contactPhone: awbData.contactPhone || contactPhone,
        contactEmail: awbData.contactEmail || contactEmail,
        issueId,
        rejectionReason: renegotiationReason,
      };

      const response = await axios.post(
        "https://server-9p4hx.kinsta.app/api/tasks/create-renegociere-report",
        payload,
      );

      if (response.status === 200) {
        const { reportPdf, pdfFilename, awbPdf, awbNumber } = response.data;

        if (awbPdf && awbNumber) {
          const awbBuffer = Uint8Array.from(atob(awbPdf), (c) =>
            c.charCodeAt(0),
          );
          const awbBlob = new Blob([awbBuffer], { type: "application/pdf" });
          const awbUrl = URL.createObjectURL(awbBlob);
          const awbLink = document.createElement("a");
          awbLink.href = awbUrl;
          awbLink.setAttribute("download", `AWB_Renegociere_${awbNumber}.pdf`);
          document.body.appendChild(awbLink);
          awbLink.click();
          awbLink.parentNode.removeChild(awbLink);
          URL.revokeObjectURL(awbUrl);
        }

        const pdfBuffer = Uint8Array.from(atob(reportPdf), (c) =>
          c.charCodeAt(0),
        );
        const pdfBlob = new Blob([pdfBuffer], { type: "application/pdf" });
        const pdfUrl = URL.createObjectURL(pdfBlob);
        const link = document.createElement("a");
        link.href = pdfUrl;
        link.setAttribute("download", pdfFilename);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        URL.revokeObjectURL(pdfUrl);

        alert("Prețul a fost refuzat. Raportul PDF a fost generat.");
      } else {
        alert("A apărut o eroare la generarea raportului pentru renegociere.");
      }
    } catch (error) {
      console.error("Eroare la generarea raportului de renegociere:", error);
      alert("A apărut o eroare la generarea raportului pentru renegociere.");
    }
  };

  // Fetch JIRA fields as defined
  const fetchJiraFields = async () => {
    try {
      const response = await axios.get(
        buildApiUrl(`/api/tasks/jira-task-qc/${issueId}/fields`),
      );
      const data = response.data;

      // Update the state with the fields you want to display
      setJiraFields({
        nume: data.nume,
        telefon: data.telefon,
        email: data.email,
        NrContract: data.NrContract,
        DataIntrare: data.DataIntrare,
        Pret: data.Pret,
        Contract: data.Contract,
      });

      // If email is present, fetch vendor data based on it
      if (data.email) {
        setVendorData(null);
        await fetchVendorData(data.email);
      } else {
        setVendorData(null);
      }
    } catch (error) {
      console.error("Error fetching JIRA fields:", error);
    }
  };

  // Fetch vendor data based on email
  const fetchVendorData = async (email) => {
    try {
      const response = await axios.get(
        buildApiUrl(
          `/api/tasks/vendor/${encodeURIComponent(email)}`,
        ),
      );
      setVendorData(response.data);
    } catch (error) {
      console.error("Error fetching vendor data:", error);
      setVendorData(null);
    }
  };

  // Funcție reutilizabilă pentru obținerea imaginii principale din Jira attachments.
  const getMainProductImageBlob = async () => {
    try {
      const res = await axios.get(
        buildApiUrl(`/api/tasks/jira-task/${issueId}`),
      );
      const attachments = res.data.attachments || [];
      console.log("📂 Attachments primite:", attachments);

      const preferredAttachmentId =
        mainImageAttachmentId || res.data.preferredMainImageAttachmentId || "";
      const selectedImage =
        res.data.mainImageAttachment ||
        selectMainJiraImageAttachment(attachments, preferredAttachmentId);

      console.log(
        "🔍 Imagine selectată din Jira attachments:",
        selectedImage,
      );

      if (selectedImage?.id) {
        console.log("📥 Fetch imagine cu ID:", selectedImage.id);
        const imageResponse = await axios.get(
          buildApiUrl(`/api/tasks/attachments/${selectedImage.id}`),
          { responseType: "arraybuffer" },
        );
        const mimeType =
          imageResponse.headers?.["content-type"] ||
          selectedImage.mimeType ||
          "image/jpeg";
        const filename = filenameForJiraImage(selectedImage, mimeType);
        return new File([imageResponse.data], filename, {
          type: mimeType,
        });
      }

      return null;
    } catch (error) {
      console.error("Eroare la obținerea imaginii principale:", error);
      return null;
    }
  };

  const saveCiornaSkuToJira = async (skuValue, { showAlert = false } = {}) => {
    const normalizedSku = String(skuValue || "").trim();

    if (!normalizedSku) {
      if (showAlert) {
        alert("SKU-ul lipsește și nu poate fi salvat în JIRA.");
      }
      return false;
    }

    try {
      console.log(
        `Saving ciornaPlatforma: ${normalizedSku} for issueId: ${issueId}`,
      );
      const response = await axios.put(
        buildApiUrl(`/api/tasks/leb-customfield/${issueId}`),
        { customFieldValue: normalizedSku },
      );

      if (response.data.success) {
        setCiornaPlatforma(normalizedSku);
        setCustomFieldValues((prevValues) => ({
          ...prevValues,
          customfield_10083: normalizedSku,
        }));

        console.log("Ciorna Platforma saved successfully");
        if (showAlert) {
          alert("Valoarea SKU a fost salvată cu succes în JIRA.");
        }
        return true;
      }

      console.error("Failed to save Ciorna Platforma");
      if (showAlert) {
        alert("A apărut o eroare la salvarea valorii SKU.");
      }
      return false;
    } catch (error) {
      console.error("Error saving Ciorna Platforma:", error);
      if (showAlert) {
        alert("A apărut o eroare la salvarea valorii SKU.");
      }
      return false;
    }
  };

  const handleCreateDraftClick = async () => {
    if (isCreatingDraft) {
      return;
    }

    if (selectedProductIds.length === 0) {
      alert("Te rog selectează un produs.");
      return;
    }
    if (!issueId) {
      alert("Lipsește issueId-ul Jira pentru preluarea imaginii principale.");
      return;
    }

    const selectedSku = selectedProductIds[0];
    const numericSuffix = selectedSku.slice(-6).replace(/\D/g, "");
    const selectedProductId = parseInt(numericSuffix, 10);
    console.log("ID-ul extras din SKU:", selectedProductId);

    const formData = new FormData();
    formData.append("productId", selectedProductId);
    if (issueId) {
      formData.append("issueId", issueId);
    }
    if (mainImageAttachmentId) {
      formData.append("attachmentId", mainImageAttachmentId);
    }

    setIsCreatingDraft(true);

    try {
      const response = await axios.post(
        buildApiUrl("/api/masterdata/clone-product-by-id"),
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      const data = response.data;

      if (data.sku) {
        setCiornaPlatforma(data.sku);
        const skuSavedInJira = await saveCiornaSkuToJira(data.sku);

        // --- START: Update SKU in productsinventory table after clonare ---
        try {
          await axios.patch(
            buildApiUrl("/api/stocmanager/products/update-sku-by-summary"),
            {
              summary: summary,
              newSku: data.sku,
            },
          );
          console.log(
            "✅ SKU actualizat în tabela productsinventory folosind endpoint PATCH:",
            data.sku,
          );
        } catch (err) {
          console.error(
            "❌ Eroare la actualizarea SKU în tabela productsinventory:",
            err,
          );
        }
        // --- END: Update SKU in productsinventory table after clonare ---
        alert(
          skuSavedInJira
            ? `Ciornă creată cu succes!\nNoul SKU este: ${data.sku}\nSKU-ul a fost salvat automat în JIRA.`
            : `Ciornă creată cu succes!\nNoul SKU este: ${data.sku}\nAtenție: SKU-ul nu a putut fi salvat automat în JIRA.`,
        );
      }
    } catch (error) {
      console.error("Eroare la clonarea produsului:", error);
      const errorData = error.response?.data || {};
      if (errorData.cloneCreated) {
        alert(
          `Ciorna a fost creată, dar poza nu a fost atașată.\nID produs: ${
            errorData.id || "-"
          }\nSKU: ${errorData.sku || "-"}\nEroare: ${
            errorData.imageError || "necunoscută"
          }`,
        );
        return;
      }
      alert(errorData.error || "A apărut o eroare la clonarea produsului.");
    } finally {
      setIsCreatingDraft(false);
    }
  };

  // Funcția care se ocupă de preluarea datelor din API-ul WooCommerce cc
  const handleCloneProduct = async (sku, consumerKey, consumerSecret) => {
    try {
      console.log("Clonare produs SKU:", sku); // Verificare SKU trimis la clonare
      const productResponse = await axios.get(
        `https://www.luxuraelite.ro/wp-json/wc/v3/products`,
        {
          params: {
            sku: sku,
          },
          headers: {
            Authorization: "Basic " + btoa(`${consumerKey}:${consumerSecret}`),
          },
        },
      );
      console.log("Răspuns WooCommerce:", productResponse); // Verifică răspunsul de la WooCommerce
      const originalProduct = productResponse.data[0];
      if (!originalProduct) {
        alert("Produsul nu a fost găsit.");
        return;
      }

      const productData = {
        name: originalProduct.name,
        price: originalProduct.price,
        regularPrice: originalProduct.regular_price,
        salePrice: originalProduct.sale_price,
        sku: originalProduct.sku,
        categories: originalProduct.categories.map((cat) => cat.name),
        description: originalProduct.description,
        shortDescription: originalProduct.short_description,
        externalColor: originalProduct.attributes.find(
          (attr) => attr.name === "Culoare",
        )?.options[0],
        interiorColor: originalProduct.attributes.find(
          (attr) => attr.name === "Material Interior",
        )?.options[0],
        exteriorMaterial: originalProduct.attributes.find(
          (attr) => attr.name === "Material Exterior",
        )?.options[0],
        condition: originalProduct.attributes.find(
          (attr) => attr.name === "Condiție",
        )?.options[0],
        length: originalProduct.dimensions.length,
        height: originalProduct.dimensions.height,
        depth: originalProduct.dimensions.width,
        includedItems: originalProduct.attributes.find(
          (attr) => attr.name === "Insotit",
        )?.options,
        designerId: originalProduct.attributes.find(
          (attr) => attr.name === "Designeri",
        )?.options[0],
        location: originalProduct.meta_data.find(
          (meta) => meta.key === "Locație",
        )?.value,
        sortlyId: originalProduct.meta_data.find(
          (meta) => meta.key === "cod_inventar_sortly",
        )?.value,
        profileImage: originalProduct.images[0]?.src,
        galleryImages: originalProduct.images
          .slice(1)
          .map((image) => image.src),
        layawayOption: originalProduct.attributes.find(
          (attr) => attr.name === "Badge",
        )?.options[0],
        isLayaway:
          originalProduct.attributes.find(
            (attr) => attr.name === "Doresc produsul prin LAYAWAY",
          )?.options.length > 0,
      };

      console.log("Product Data:", productData); // Afișează datele produsului
      return productData;
    } catch (error) {
      console.error("Eroare la preluarea datelor produsului:", error);
      throw error;
    }
  };

  const [customFields, setCustomFields] = useState({
    brand: "",
    categorie: "",
    model: "",
  });
  const categoryDisplayValue = normalizeCategoryDisplay(categoryLabel, category);

  const handleFetchCiornaCategoryFields = useCallback(async () => {
    try {
      const response = await axios.get(
        `https://server-9p4hx.kinsta.app/api/tasks/leb-customfield-forciorna/${issueId}`,
      );
      const fetchedFields = response.data?.customFields || {};

      setCustomFields((prev) => ({
        ...prev,
        categorie:
          normalizeCategoryDisplay(
            fetchedFields.categorie,
            categoryDisplayValue,
            prev.categorie,
          ) || "Not available",
        stare: fetchedFields.stare || "Not available",
        descriereSpecifica:
          fetchedFields.descriereSpecifica || "Not available",
      }));
    } catch (error) {
      console.error("Error fetching ciorna category fields:", error);
    }
  }, [categoryDisplayValue, issueId]);

  const handleExtractBrandModelFromSummary = useCallback(async () => {
    const requestId = brandModelRequestRef.current + 1;
    brandModelRequestRef.current = requestId;
    const cleanedSummary = cleanSummaryForBrandModel(summary);

    setCustomFields((prev) => ({
      ...prev,
      brand: "",
      model: "",
      categorie: categoryDisplayValue || prev.categorie,
    }));

    if (!cleanedSummary) {
      return;
    }

    const fallback = fallbackBrandModelFromSummary(cleanedSummary);

    try {
      const prompt = [
        "Extrage brandul si modelul produsului din titlul de mai jos.",
        "Titlul poate proveni dintr-un summary Jira si ID-ul numeric initial a fost deja eliminat.",
        'Returneaza exclusiv JSON valid, fara markdown, exact cu cheile "brand" si "model".',
        "Brandul este producatorul/designerul, inclusiv branduri din doua cuvinte precum Louis Vuitton, Bottega Veneta, Saint Laurent.",
        "Modelul este denumirea comerciala ramasa, fara brand si fara ID numeric. Nu inventa informatii.",
        `Titlu: ${cleanedSummary}`,
      ].join("\n");

      const { data } = await axios.post(
        AI_REPHRASE_URL,
        { prompt },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      const parsed = parseAiJsonObject(data?.content);
      const brand = normalizeAiField(parsed?.brand) || fallback.brand;
      const model = normalizeAiField(parsed?.model) || fallback.model;

      if (requestId !== brandModelRequestRef.current) {
        return;
      }

      setCustomFields((prev) => ({
        ...prev,
        brand,
        model,
        categorie: categoryDisplayValue || prev.categorie,
      }));
    } catch (error) {
      console.error("Error extracting brand/model from summary with AI:", error);
      if (requestId !== brandModelRequestRef.current) {
        return;
      }
      setCustomFields((prev) => ({
        ...prev,
        brand: fallback.brand,
        model: fallback.model,
        categorie: categoryDisplayValue || prev.categorie,
      }));
    }
  }, [categoryDisplayValue, summary]);

  const sanitizeAttributeOptions = (values = []) =>
    Array.from(
      new Set(
        (values || [])
          .map((value) =>
            typeof value === "string" ? value.trim() : String(value || ""),
          )
          .filter((value) => Boolean(value) && value !== "undefined"),
      ),
    );
  const splitAttributeValues = (rawValue = "") =>
    String(rawValue || "")
      .split(/[\n\r,;|/]+/)
      .map((token) => token.replace(/\s+/g, " ").trim())
      .filter(Boolean);
  const sanitizeCategoryToken = (value = "") => {
    const trimmed = value.replace(/\s+/g, " ").trim();
    if (!trimmed) {
      return "";
    }
    return trimmed.toLowerCase() === "fara categorie" ? "" : trimmed;
  };
  const extractWooCategoryNames = (items = []) =>
    (Array.isArray(items) ? items : [])
      .map((cat) => cat?.name || cat?.label || cat?.value || "")
      .map(sanitizeCategoryToken)
      .filter(Boolean);
  const normalizeSelectedWooCategories = (items = []) =>
    (Array.isArray(items) ? items : [])
      .map((cat) => {
        const rawId =
          cat?.id !== undefined && cat?.id !== null
            ? cat.id
            : cat?.value !== undefined && cat?.value !== null
              ? cat.value
              : cat;
        const numericId = Number(rawId);
        if (!numericId || Number.isNaN(numericId)) {
          return null;
        }
        const ancestorsSource = Array.isArray(cat?.ancestors)
          ? cat.ancestors
          : [];
        const normalizedAncestors = ancestorsSource
          .map((ancestorId) => Number(ancestorId))
          .filter(
            (ancestorId) => Number.isInteger(ancestorId) && ancestorId > 0,
          );
        return {
          id: numericId,
          name: cat?.name || cat?.label || "",
          ancestors: normalizedAncestors,
        };
      })
      .filter(Boolean);

  const syncWooProductDetails = async ({
    sku,
    conditionValue,
    formattedDescription,
    normalizedTags = [],
    normalizedBadges = [],
    details = {},
    priceValue,
    productTitle,
  }) => {
    const trimmedSku = (sku || "").trim();
    if (!trimmedSku) {
      console.warn("[WooSync] SKU lipsă, nu sincronizăm cu WooCommerce.");
      return;
    }

    try {
      const { data } = await axios.get(`${WOO_PROXY_BASE}/product`, {
        params: { sku: trimmedSku },
      });

      let wooProduct = data || undefined;

      if (!wooProduct) {
        const numericIdRaw = trimmedSku.replace(/\D/g, "");
        const numericId = numericIdRaw ? parseInt(numericIdRaw, 10) : null;
        if (numericId) {
          try {
            const productById = await axios.get(`${WOO_PROXY_BASE}/product`, {
              params: { id: numericId },
            });
            wooProduct = productById.data || undefined;
          } catch (idError) {
            console.warn(
              `[WooSync] Nu am putut găsi produsul prin SKU; încercarea folosind ID ${numericId} a eșuat.`,
              idError?.response?.data || idError.message,
            );
          }
        }
      }

      if (!wooProduct) {
        console.warn(
          `[WooSync] Nu am găsit produsul în WooCommerce pentru SKU ${trimmedSku}.`,
        );
        return;
      }

      const normalizedCategory = normalizeSimple(details.categorieProd);
      const normalizedSubcategory = normalizeSimple(details.subcategorie);
      const categoryMatches = (needle) =>
        normalizedMatchesToken(normalizedCategory, needle);
      const isAccessoryCategory = categoryMatches("accesorii");
      const isAccessoryScarf =
        isAccessoryCategory && normalizedSubcategory.includes("esarfa");
      const isAccessoryApparel = categoryMatches("imbracaminte");
      const isAccessoryHat =
        isAccessoryCategory &&
        (normalizedSubcategory.includes("hat") ||
          normalizedSubcategory.includes("hats") ||
          normalizedSubcategory.includes("palar") ||
          normalizedSubcategory.includes("palari") ||
          normalizedSubcategory.includes("har"));
      const isAccessoryWrap =
        isAccessoryScarf || isAccessoryHat || isAccessoryApparel;
      const normalizedWooSelections = normalizeSelectedWooCategories(
        details.wooCategoriesSelected,
      );

      const mergedAttributes = Array.isArray(wooProduct.attributes)
        ? JSON.parse(JSON.stringify(wooProduct.attributes))
        : [];
      let attributesChanged = false;
      const removeAttribute = (key) => {
        const config = WOO_ATTRIBUTE_CONFIG[key];
        if (!config) {
          return;
        }
        const idx = mergedAttributes.findIndex(
          (attr) =>
            Number(attr?.id) === config.id ||
            (attr?.name || "").toLowerCase() === config.name.toLowerCase(),
        );
        if (idx >= 0) {
          mergedAttributes.splice(idx, 1);
          attributesChanged = true;
        }
      };

      const applyAttribute = (key, values) => {
        const config = WOO_ATTRIBUTE_CONFIG[key];
        if (!config) {
          return;
        }
        const normalized = sanitizeAttributeOptions(values);
        if (!normalized.length) {
          return;
        }
        const idx = mergedAttributes.findIndex(
          (attr) =>
            Number(attr?.id) === config.id ||
            (attr?.name || "").toLowerCase() === config.name.toLowerCase(),
        );
        if (idx >= 0) {
          const previous = (mergedAttributes[idx].options || []).join("|");
          const next = normalized.join("|");
          if (previous !== next) {
            mergedAttributes[idx] = {
              ...mergedAttributes[idx],
              id: mergedAttributes[idx].id || config.id,
              name: mergedAttributes[idx].name || config.name,
              options: normalized,
              visible:
                typeof mergedAttributes[idx].visible === "boolean"
                  ? mergedAttributes[idx].visible
                  : true,
              variation:
                typeof mergedAttributes[idx].variation === "boolean"
                  ? mergedAttributes[idx].variation
                  : false,
            };
            attributesChanged = true;
          }
        } else {
          mergedAttributes.push({
            id: config.id,
            name: config.name,
            options: normalized,
            visible: true,
            variation: false,
          });
          attributesChanged = true;
        }
      };
      const getExistingAttributeValues = (key) => {
        const config = WOO_ATTRIBUTE_CONFIG[key];
        if (!config) return [];
        const attr = mergedAttributes.find(
          (item) =>
            Number(item?.id) === config.id ||
            (item?.name || "").toLowerCase() === config.name.toLowerCase(),
        );
        return sanitizeAttributeOptions(attr?.options || []);
      };
      const mergeWithExisting = (key, values) =>
        sanitizeAttributeOptions([
          ...(Array.isArray(values) ? values : []),
          ...getExistingAttributeValues(key),
        ]);

      const normalizedCondition = sanitizeAttributeOptions([conditionValue]);
      const conditionValues = normalizedCondition.length
        ? normalizedCondition
        : getExistingAttributeValues("condition");
      applyAttribute("condition", conditionValues);

      const designerValues = mergeWithExisting("designers", [
        details.brand || customFields.brand || "",
      ]);
      applyAttribute("designers", designerValues);

      const badgeValues = mergeWithExisting("badge", normalizedBadges);
      applyAttribute("badge", badgeValues);

      const includedValues = mergeWithExisting("included", normalizedTags);
      applyAttribute("included", includedValues);

      const targetAudienceValues = mergeWithExisting("targetAudience", [
        details.pentru || "Femei",
      ]);
      applyAttribute("targetAudience", targetAudienceValues);
      const exteriorColorValues = splitAttributeValues(
        details.culoareExterioara || "",
      );
      const interiorColorValues = splitAttributeValues(
        details.culoareInterioara || "",
      );
      const colorValues = sanitizeAttributeOptions(
        isAccessoryWrap
          ? exteriorColorValues
          : [...exteriorColorValues, ...interiorColorValues],
      );
      if (colorValues.length) {
        applyAttribute("color", colorValues);
      } else {
        removeAttribute("color");
      }
      applyAttribute(
        "designerId",
        details.designerId ? [details.designerId] : [],
      );
      applyAttribute("entrupy", details.entrupyId ? [details.entrupyId] : []);
      const existingCategoryAttributes = Array.isArray(
        details.existingCategoryAttributes,
      )
        ? details.existingCategoryAttributes.filter(Boolean)
        : [];
      const categoryTokens = splitAttributeValues(details.categorieProd || "")
        .map(sanitizeCategoryToken)
        .filter(Boolean);
      const combinedCategories = [
        ...existingCategoryAttributes,
        ...categoryTokens,
        ...extractWooCategoryNames(details.wooCategoriesSelected),
      ];
      if (combinedCategories.length) {
        applyAttribute("category", combinedCategories);
      } else {
        removeAttribute("category");
      }
      const existingSubcategoryAttributes = Array.isArray(
        details.existingSubcategoryAttributes,
      )
        ? details.existingSubcategoryAttributes.filter(Boolean)
        : [];
      const subcategoryTokens = splitAttributeValues(details.subcategorie || "")
        .map(sanitizeCategoryToken)
        .filter(Boolean);
      const combinedSubcategories = [
        ...existingSubcategoryAttributes,
        ...subcategoryTokens,
      ];
      if (combinedSubcategories.length) {
        applyAttribute("subcategory", combinedSubcategories);
      } else {
        removeAttribute("subcategory");
      }
      const sanitizeFootwearToken = (token = "") =>
        String(token || "")
          .replace(/\s*cm$/i, "")
          .trim();
      const apparelSizeTokens = splitAttributeValues(
        details.marimeDimensiune || "",
      );
      const footwearSizeTokens = splitAttributeValues(
        details.lungime || details.marimeDimensiune || "",
      ).map(sanitizeFootwearToken);
      const isFootwearCategory = categoryMatches("incaltaminte");
      if (isAccessoryApparel) {
        if (apparelSizeTokens.length) {
          applyAttribute("apparelSize", apparelSizeTokens);
        } else {
          removeAttribute("apparelSize");
        }
        removeAttribute("footwearSize");
      } else if (isFootwearCategory) {
        if (footwearSizeTokens.length) {
          applyAttribute("footwearSize", footwearSizeTokens);
        } else {
          removeAttribute("footwearSize");
        }
        removeAttribute("apparelSize");
      } else {
        removeAttribute("apparelSize");
        removeAttribute("footwearSize");
      }
      const normalizeDimensionToken = (value = "") =>
        String(value || "")
          .replace(/\s+/g, " ")
          .trim();
      const buildDimensionValue = () => {
        if (isAccessoryApparel) {
          const parts = [];
          const apparelSize = normalizeDimensionToken(
            details.marimeDimensiune || "",
          );
          const bust = normalizeDimensionToken(details.bustDimensiune || "");
          const sleeve = normalizeDimensionToken(
            details.sleeveDimensiune || "",
          );
          const shoulder = normalizeDimensionToken(
            details.shoulderDimensiune || "",
          );
          const length = normalizeDimensionToken(details.lungime || "");
          if (apparelSize) parts.push(`Mărime: ${apparelSize}`);
          if (bust) parts.push(`Bust: ${bust}`);
          if (sleeve) parts.push(`Mânecă: ${sleeve}`);
          if (shoulder) parts.push(`Umăr: ${shoulder}`);
          if (length) parts.push(`Lungime: ${length}`);
          return parts.join(" | ");
        }
        if (isAccessoryHat) {
          const parts = [];
          if (apparelSizeTokens[0]) {
            parts.push(`Mărime: ${apparelSizeTokens[0]}`);
          }
          const circumference = normalizeDimensionToken(details.lungime || "");
          if (circumference) {
            parts.push(`Circumferință: ${circumference}`);
          }
          const hatHeight = normalizeDimensionToken(details.inaltime || "");
          if (hatHeight) {
            parts.push(`Înălțime: ${hatHeight}`);
          }
          return parts.join(" | ");
        }
        const sizeValue = normalizeDimensionToken(
          details.marimeDimensiune || "",
        );
        return sizeValue || "";
      };
      const dimensionValue = buildDimensionValue();
      if (dimensionValue) {
        applyAttribute(
          "dimension",
          mergeWithExisting("dimension", [dimensionValue]),
        );
      } else {
        const existingDimension = getExistingAttributeValues("dimension");
        if (existingDimension.length) {
          applyAttribute("dimension", existingDimension);
        } else {
          removeAttribute("dimension");
        }
      }

      const updateBody = {};
      const normalizedMsrp =
        details.msrpPrice === undefined || details.msrpPrice === null
          ? ""
          : String(details.msrpPrice).trim();
      if (attributesChanged) {
        updateBody.attributes = mergedAttributes;
        console.info("[WooSync][Attributes] Prepared attributes payload", {
          sku: trimmedSku,
          productId: wooProduct.id,
          count: mergedAttributes.length,
          attributes: mergedAttributes.map((attr) => ({
            id: attr?.id ?? null,
            name: attr?.name || "",
            slug: attr?.slug || "",
            visible: typeof attr?.visible === "boolean" ? attr.visible : true,
            variation:
              typeof attr?.variation === "boolean" ? attr.variation : false,
            options: Array.isArray(attr?.options) ? attr.options : [],
          })),
        });
      }
      const desiredWooCategoryIds = Array.from(
        new Set(
          normalizedWooSelections.flatMap((cat) => [
            cat.id,
            ...(Array.isArray(cat.ancestors) ? cat.ancestors : []),
          ]),
        ),
      );
      const hadExistingCategories =
        Array.isArray(wooProduct.categories) &&
        wooProduct.categories.length > 0;
      if (desiredWooCategoryIds.length) {
        updateBody.categories = desiredWooCategoryIds.map((id) => ({ id }));
      } else if (hadExistingCategories) {
        updateBody.categories = [];
      }

      const normalizedPrice = String(priceValue || "")
        .replace(/\s+/g, "")
        .trim();
      if (
        normalizedPrice &&
        normalizedPrice !== String(wooProduct.regular_price || "")
      ) {
        updateBody.regular_price = normalizedPrice;
      }
      if (
        normalizedMsrp &&
        normalizedMsrp !== String(wooProduct.msrp_price || "")
      ) {
        updateBody.msrp_price = normalizedMsrp;
      }

      const incomingDimensions = {
        length: (details.lungime || "").trim(),
        height: (details.inaltime || "").trim(),
        width: (details.adancime || "").trim(),
      };
      const currentDimensions = {
        length: wooProduct.dimensions?.length || "",
        height: wooProduct.dimensions?.height || "",
        width: wooProduct.dimensions?.width || "",
      };
      const dimensionsChanged =
        incomingDimensions.length !== currentDimensions.length ||
        incomingDimensions.height !== currentDimensions.height ||
        incomingDimensions.width !== currentDimensions.width;
      if (dimensionsChanged) {
        updateBody.dimensions = incomingDimensions;
      }

      const inferredFootwear =
        /pantof|sneaker|boot|shoe|încălțăminte|incaltaminte/i.test(
          `${details.categorieProd || ""} ${details.subcategorie || ""}`,
        );
      const brandValue = (details.brand || "").trim().toUpperCase();
      const badgeLabel = normalizedBadges?.[0] || "";
      const baseTitle =
        (details.model || "").trim() ||
        (productTitle || "").trim() ||
        summary?.trim() ||
        wooProduct?.name?.replace(/<[^>]+>/g, "").trim() ||
        "";
      const htmlTitle = `
            <span style="font-size: 10px; color: rgb(101, 101, 101);letter-spacing: .15em;">${
              badgeLabel || ""
            }</span><br>
            <strong>${brandValue || ""}</strong><br>
            <div style="margin-top:10px; font-size:16px;">${baseTitle}</div><br>
        `;
      const footwearItemSize = inferredFootwear
        ? details.lungime || details.marimeDimensiune || ""
        : details.marimeDimensiune || "";
      const htmlDescription = buildHtmlContent({
        conditionValue: CONDITION_VALUE_MAP[conditionValue] || 0,
        conditionLabel: conditionValue || "",
        customDescription: formattedDescription || "",
        exteriorColor: details.culoareExterioara || "",
        interiorColor: details.culoareInterioara || "",
        exteriorMaterial: details.materialExterior || "",
        interiorMaterial: details.materialInterior || "",
        includedItems: normalizedTags,
        hardwareColor: details.culoareHW || "",
        designerId: details.designerId || "",
        entrupyId: details.entrupyId || "",
        location: details.location || "",
        length: details.lungime || "",
        height: details.inaltime || "",
        depth: details.adancime || "",
        detailedDescription: formattedDescription || "",
        sortlyId: "",
        salePrice: normalizedPrice,
        price: normalizedPrice,
        brand: brandValue,
        layawayPlan: details.layawayEnabled ? details.layawayOption || "" : "",
        apparelMeasurements: categoryMatches("imbracaminte")
          ? [
              { label: "Mărime", value: details.marimeDimensiune || "" },
              { label: "Bust", value: details.bustDimensiune || "" },
              { label: "Mânecă", value: details.sleeveDimensiune || "" },
              { label: "Umăr", value: details.shoulderDimensiune || "" },
              { label: "Lungime", value: details.lungime || "" },
            ]
          : [],
        itemSize: footwearItemSize,
        sizeValue: details.marimeDimensiune || "",
        isFootwear: inferredFootwear,
        isAccessoryScarf,
        isAccessoryHat,
        footwearSizeNote: inferredFootwear
          ? details.marimeDimensiune || ""
          : "",
      });
      if (htmlDescription) {
        const desiredDescriereContent =
          (details.wooDescription && details.wooDescription.trim()) ||
          formattedDescription ||
          "";
        const descriptionWithCustomDescriere = replaceDescriereContent(
          htmlDescription,
          desiredDescriereContent,
        );
        const currentDescription = (wooProduct.description || "").trim();
        const nextDescription = descriptionWithCustomDescriere.trim();
        if (currentDescription !== nextDescription) {
          updateBody.description = nextDescription;
        }
      }

      if (
        htmlTitle.trim() &&
        (wooProduct.name || "").trim() !== htmlTitle.trim()
      ) {
        updateBody.name = htmlTitle;
      }

      if (Array.isArray(wooProduct.meta_data)) {
        const meta = JSON.parse(JSON.stringify(wooProduct.meta_data));
        const ensureMeta = (key, value) => {
          const normalizedKey = (key || "").toLowerCase();
          const idx = meta.findIndex(
            (entry) => (entry?.key || "").toLowerCase() === normalizedKey,
          );
          const safeValue = value || "";
          if (!safeValue) {
            return false;
          }
          if (idx >= 0) {
            if ((meta[idx].value || "") !== safeValue) {
              meta[idx].value = safeValue;
              return true;
            }
            return false;
          }
          meta.push({ key, value: safeValue });
          return true;
        };

        const desiredLocation = (details.location || "").trim();
        const rawCategory = (details.categorieProd || "").trim();
        const normalizedCategory = normalizeSimple(rawCategory);
        const categoryMatchesMeta = (needle) =>
          normalizedMatchesToken(normalizedCategory, needle);
        let metaChanged = false;

        if (desiredLocation) {
          if (ensureMeta(WOO_LOCATION_META_KEY, desiredLocation)) {
            metaChanged = true;
          }
        }

        if (normalizedMsrp) {
          if (ensureMeta("msrp_price", normalizedMsrp)) {
            metaChanged = true;
          }
        }

        const brandModel =
          `${brandValue || ""} ${(details.model || "").trim()}`.trim();
        if (brandModel) {
          if (ensureMeta("_yoast_wpseo_focuskw", brandModel)) {
            metaChanged = true;
          }
          if (ensureMeta("_yoast_wpseo_title", brandModel)) {
            metaChanged = true;
          }
        }

        const isHandbagCategory =
          categoryMatchesMeta("genti") || categoryMatchesMeta("geanta");
        const isFootwearCategory = categoryMatchesMeta("incaltaminte");
        let metaDescription = "";
        if (isHandbagCategory) {
          metaDescription = `Descoperă ${brandValue || ""} ${(details.model || "").trim() || ""} pre-owned. Descoperă o selecție diversificată de stiluri de genți ${brandValue || ""}.`;
        } else if (isFootwearCategory) {
          const upperBrand = (brandValue || "").toUpperCase();
          metaDescription = `Descoperă ${brandValue || ""} ${(details.model || "").trim() || ""} pre-owned. Descoperă o selecție diversificată de stiluri de încălțăminte ${upperBrand}. Verifică acum disponibilitatea!`;
        }
        metaDescription = metaDescription.replace(/\s+/g, " ").trim();
        if (metaDescription) {
          if (ensureMeta("_yoast_wpseo_metadesc", metaDescription)) {
            metaChanged = true;
          }
          if (
            ensureMeta("_yoast_wpseo_opengraph-description", metaDescription)
          ) {
            metaChanged = true;
          }
          if (ensureMeta("_yoast_wpseo_twitter-description", metaDescription)) {
            metaChanged = true;
          }
        }
        if (brandModel) {
          if (ensureMeta("_yoast_wpseo_opengraph-title", brandModel)) {
            metaChanged = true;
          }
          if (ensureMeta("_yoast_wpseo_twitter-title", brandModel)) {
            metaChanged = true;
          }
        }

        if (metaChanged) {
          updateBody.meta_data = meta;
        }
      }

      const hasBodyChanges =
        Boolean(updateBody.attributes) ||
        updateBody.categories !== undefined ||
        updateBody.regular_price !== undefined ||
        updateBody.msrp_price !== undefined ||
        updateBody.dimensions !== undefined ||
        updateBody.description !== undefined ||
        updateBody.meta_data !== undefined;
      if (!hasBodyChanges) {
        console.log(
          "[WooSync] Nu s-au identificat modificări pentru a actualiza detaliile de bază.",
        );
      } else {
        if (updateBody.attributes) {
          console.info("[WooSync][Attributes] Sending updateBody.attributes", {
            sku: trimmedSku,
            productId: wooProduct.id,
            count: updateBody.attributes.length,
          });
        }
        await axios.post(
          `${WOO_PROXY_BASE}/product/${wooProduct.id}`,
          updateBody,
        );
        console.log(
          `[WooSync] Produsul cu SKU ${trimmedSku} a fost sincronizat în WooCommerce.`,
        );
      }

      await syncLayawayPlan({
        productId: wooProduct.id,
        enabled: Boolean(details.layawayEnabled),
        plan: details.layawayOption || "",
      });
      if (desiredWooCategoryIds.length || hadExistingCategories) {
        try {
          await axios.post(
            "https://server-9p4hx.kinsta.app/api/masterdata/update-woo-categories",
            {
              productId: wooProduct.id,
              sku: wooProduct.sku || trimmedSku,
              categories: desiredWooCategoryIds,
            },
          );
          console.log(
            `[WooSync] Categoriile au fost sincronizate prin backend pentru SKU ${trimmedSku}.`,
          );
        } catch (catSyncErr) {
          console.error(
            "[WooSync] Eroare la actualizarea categoriilor prin backend:",
            catSyncErr?.response?.data || catSyncErr.message,
          );
        }
      }
    } catch (error) {
      console.error(
        "[WooSync] Eroare la sincronizarea cu WooCommerce:",
        error?.response?.data || error.message,
      );
      throw error;
    }
  };

  // În componenta CreateProductWoo, poți accesa productData astfel:
  useEffect(() => {
    const productData = window.opener ? window.opener.productData : null;
    if (productData) {
      // Poți face ceva cu productData aici, cum ar fi să-l afișezi
      console.log("Received productData:", productData);
    }
  }, []);

  useEffect(() => {
    handleFetchCiornaCategoryFields();
  }, [handleFetchCiornaCategoryFields]);

  useEffect(() => {
    handleExtractBrandModelFromSummary();
  }, [handleExtractBrandModelFromSummary]);

  useEffect(() => {
    const fetchLastProductId = async () => {
      try {
        const response = await axios.get(
          "https://www.luxuraelite.ro/wp-json/wc/v3/products",
          {
            params: {
              per_page: 1,
              orderby: "id",
              order: "desc",
            },
            headers: {
              Authorization:
                "Basic " + btoa(`${consumerKey}:${consumerSecret}`),
            },
          },
        );
        const lastProductId = response.data[0]?.id || 0;
        setNextProductId(lastProductId + 1);
      } catch (error) {
        console.error("Error fetching last product ID:", error);
      }
    };

    fetchLastProductId();
  }, []);

  const resetState = () => {
    setExpanded(false);
    setCondition("");
    setDescription("");
    setQcStatus("");
    setFisaProdusStatus("");
    setNewPrice("");
    setAnchorEl(null);
    setOpenReportModal(false);
    setAwbData({
      street: "",
      number: "",
      postalCode: "",
      city: "",
      county: "",
      country: "Romania",
      observations: "",
      contactName: "",
      contactPhone: "",
      contactEmail: "",
      pickupDate: "",
      scheduleDate: "",
    });
    setRejectionReason("");
    setSesiuniDisponibile([]);
    setSelectedSesiune("");
    setLinkedIssues([]);
    setFisaProdusId("");
    setFisaProdusIdStatus("");
    setCiornaPlatforma("Not available");
    setWooCommerceTitle("");
    setWooCommerceStatus("");
    setSelectedTags([]);
    setAvailableTags([]);
    setAvailableBadges([]);
    setSelectedBadges([]);
    setProductDetails(initialProductDetails);
    productDetailsRef.current = initialProductDetails;
    setProductDetailOptions({ trimisLaPozat: [], categorieProd: [] });
    setStockProducts([]);
    setSyncToPlatform(true);
    setWooProductSnapshot(null);
    setWooReplayToken(0);
    setJiraProductSnapshot(initialProductDetails);
    setHasLoadedJiraSnapshot(false);
    setHasLoadedWooSnapshot(false);
    setIsInitialFisaLoading(true);
  };

  useEffect(() => {
    console.log("issueId s-a schimbat:", issueId);
    resetState();
    fetchQcStatus();
    fetchCiornaPlatforma();
  }, [issueId]);

  const fetchProdusStatus = async (retryCount = 0) => {
    if (!summary) {
      console.warn(
        "[Fișa Produs] Nu avem summary pentru a căuta taskul din PROD.",
      );
      return null;
    }

    const encodedSummary = encodeURIComponent(summary);

    try {
      const response = await axios.get(
        buildApiUrl(`/api/tasks/prod-status/${encodedSummary}`),
      );
      console.log("[Fisa Produs] PROD status response", {
        summary,
        data: response.data,
      });
      setFisaProdusStatus(response.data.status || "Status necunoscut");
      setCondition(response.data.condition || "");
      setDescription(response.data.description || "");
      setFisaProdusId(response.data.id);
      setFisaProdusIdStatus(response.data.key);
      return response.data;
    } catch (error) {
      console.error("Error fetching product status:", error);

      if (retryCount < 2) {
        console.log(`Retrying fetchProdusStatus... Attempt ${retryCount + 1}`);
        setTimeout(() => fetchProdusStatus(retryCount + 1), 1000); // Așteaptă 1 secundă înainte de următoarea încercare
      } else {
        console.error("Max retry attempts reached for fetchProdusStatus");
      }
      return null;
    }
  };

  useEffect(() => {
    if (summary) {
      fetchProdusStatus();
    }
  }, [summary]);

  const fetchQcStatus = async () => {
    try {
      console.log("Fetching QC status pentru issueId:", issueId);
      const response = await axios.get(
        `https://server-9p4hx.kinsta.app/api/tasks/jira-task/${issueId}`,
      );
      const fetchedStatus = response.data.customfield_10188 || "";
      qcStatusRef.current = fetchedStatus;
      setQcStatus(fetchedStatus);
    } catch (error) {
      console.error("Error fetching QC status:", error);
    }
  };

  useEffect(() => {
    getRenegotiationQcDetails();
    fetchJiraFields();
    fetchCiornaPlatforma(); // re-citește SKU (10083) la fiecare schimbare de task
  }, [issueId]);

  useEffect(() => {
    fetchSesiuniDisponibile();
  }, []);

  const fetchCiornaPlatforma = async (retryCount = 0) => {
    try {
      console.log(
        `Fetching ciornaPlatforma for issueId: ${issueId} (attempt ${
          retryCount + 1
        })`,
      );

      // Încearcă mai întâi să citești direct din JIRA (customfield_10083)
      let jiraError = null;
      try {
        const jiraResp = await axios.get(
          `https://server-9p4hx.kinsta.app/api/tasks/jira-task/${issueId}`,
        );
        const skuFromJira = jiraResp.data?.customfield_10083 || "";
        if (skuFromJira) {
          console.log(
            `Fetched ciornaPlatforma direct din JIRA 10083: ${skuFromJira}`,
          );
          setCiornaPlatforma(skuFromJira);
          return;
        }
      } catch (jiraErr) {
        jiraError = jiraErr;
      }

      if (retryCount < 2) {
        console.log(
          `Retrying ciornaPlatforma from JIRA... Attempt ${retryCount + 2}`,
        );
        setTimeout(() => fetchCiornaPlatforma(retryCount + 1), 1000);
        return;
      }

      if (jiraError) {
        console.warn(
          "Fallback to leb-customfield for ciornaPlatforma",
          jiraError,
        );
      }

      // Fallback pe endpoint-ul existent
      const response = await axios.get(
        `https://server-9p4hx.kinsta.app/api/tasks/leb-customfield/${issueId}`,
      );
      const fallback = response.data.customFieldValue || "";
      console.log(`Fetched ciornaPlatforma fallback: ${fallback}`);
      setCiornaPlatforma(fallback || "Not available");
    } catch (error) {
      console.error("Error fetching Ciorna Platforma:", error);
    }
  };

  useEffect(() => {
    if (!enableWooFetchInParent) {
      setWooProductSnapshot(null);
      setIsWooDetailsLoading(false);
      setHasLoadedWooSnapshot(true);
      return;
    }
    const fetchWooCommerceProduct = async (retryCount = 0) => {
      setIsWooDetailsLoading(true);
      try {
        const url = `${WOO_API_BASE}/products?sku=${ciornaPlatforma}&consumer_key=${WOO_CONSUMER_KEY}&consumer_secret=${WOO_CONSUMER_SECRET}`;

        console.log(
          "Attempting to fetch product from WooCommerce with URL:",
          url,
        );

        const response = await axios.get(url);

        if (response.data.length > 0) {
          const product = response.data[0];
          const metaData = Array.isArray(product?.meta_data)
            ? product.meta_data
            : [];
          const normalizeKey = (value = "") =>
            String(value || "")
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "")
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "")
              .trim();
          const findMetaValue = (keys = []) => {
            const normalizedKeys = keys.map((key) => normalizeKey(key));
            const match = metaData.find((meta) =>
              normalizedKeys.includes(normalizeKey(meta?.key || "")),
            );
            return match?.value ?? "";
          };
          const resolvedWooPriceRaw =
            product.price ||
            product.regular_price ||
            product.sale_price ||
            findMetaValue([
              "_price",
              "_regular_price",
              "_sale_price",
              "price",
              "regular_price",
              "sale_price",
            ]) ||
            "";
          const hasPrice = String(resolvedWooPriceRaw || "").trim() !== "";
          if (!hasPrice && retryCount < 2) {
            console.log(
              `[Fisa Produs] Woo price missing, retrying... Attempt ${
                retryCount + 2
              }`,
            );
            setTimeout(() => fetchWooCommerceProduct(retryCount + 1), 1000);
            return;
          }
          setWooProductSnapshot(product);
          const cleanTitle = extractTitle(product.name);
          setWooCommerceTitle(cleanTitle);
          setWooCommerceStatus(product.status);
          console.log("[Fisa Produs] Woo snapshot loaded", {
            sku: product?.sku || ciornaPlatforma,
            id: product?.id,
          });
        } else {
          if (retryCount < 2) {
            console.log(
              `[Fisa Produs] Woo snapshot empty, retrying... Attempt ${
                retryCount + 2
              }`,
            );
            setTimeout(() => fetchWooCommerceProduct(retryCount + 1), 1000);
            return;
          }
          setWooCommerceTitle("Product not found");
          setWooCommerceStatus("Unavailable");
          setWooProductSnapshot(null);
          setIsWooDetailsLoading(false);
          console.log("[Fisa Produs] Woo snapshot not found", {
            sku: ciornaPlatforma,
          });
        }
      } catch (error) {
        console.error("Error fetching product from WooCommerce:", error);
        if (retryCount < 2) {
          console.log(
            `[Fisa Produs] Woo fetch error, retrying... Attempt ${
              retryCount + 2
            }`,
          );
          setTimeout(() => fetchWooCommerceProduct(retryCount + 1), 1000);
          return;
        }
        setWooProductSnapshot(null);
        setIsWooDetailsLoading(false);
      } finally {
        setHasLoadedWooSnapshot(true);
      }
    };

    if (ciornaPlatforma && ciornaPlatforma !== "Not available") {
      setHasLoadedWooSnapshot(false);
      fetchWooCommerceProduct();
    } else {
      setWooProductSnapshot(null);
      setIsWooDetailsLoading(false);
      setHasLoadedWooSnapshot(true);
    }
  }, [ciornaPlatforma, enableWooFetchInParent]);

  useEffect(() => {
    if (wooProductSnapshot) {
      applyWooProductDetailsFromWoo(wooProductSnapshot);
      setIsWooDetailsLoading(false);
    } else {
      setIsWooDetailsLoading(false);
    }
  }, [wooProductSnapshot, applyWooProductDetailsFromWoo, wooReplayToken]);

  useEffect(() => {
    const needsWoo = syncToPlatform;
    const jiraReady = hasLoadedJiraSnapshot;
    const wooReady = !needsWoo || hasLoadedWooSnapshot;
    const isLoading = !(jiraReady && wooReady);
    setIsInitialFisaLoading(isLoading);
    console.log("[Fisa Produs] initial load", {
      jiraReady,
      wooReady,
      syncToPlatform,
      isLoading,
    });
  }, [hasLoadedJiraSnapshot, hasLoadedWooSnapshot, syncToPlatform]);

  const handleRequestWooRefresh = useCallback(() => {
    if (!enableWooFetchInParent) {
      return;
    }
    if (wooProductSnapshot) {
      applyWooProductDetailsFromWoo(wooProductSnapshot);
    }
  }, [
    wooProductSnapshot,
    applyWooProductDetailsFromWoo,
    enableWooFetchInParent,
  ]);

  const fetchAvailableTags = async () => {
    try {
      if (fisaProdusId) {
        const response = await axios.get(
          buildApiUrl(`/api/tasks/jira-available-tags/${fisaProdusId}`),
        );
        console.log("[Fisa Produs] JIRA available tags response", {
          fisaProdusId,
          data: response.data,
        });
        const { availableTags, selectedTags } = response.data;
        if (Array.isArray(availableTags) && Array.isArray(selectedTags)) {
          setAvailableTags(availableTags);
          setSelectedTags(selectedTags);
        } else {
          setAvailableTags([]);
          setSelectedTags([]);
        }
      }
    } catch (error) {
      console.error("Error fetching available tags:", error);
    }
  };

  useEffect(() => {
    fetchAvailableTags();
  }, [fisaProdusId]);

  useEffect(() => {
    if (fisaProdusId) {
      fetchLinkedIssues(fisaProdusId);
    }
  }, [fisaProdusId]);

  const handleSaveCiornaPlatforma = async () => {
    await saveCiornaSkuToJira(ciornaPlatforma, { showAlert: true });
  };

  const loadInvestitiiPools = useCallback(async () => {
    setInvestitiiPoolsLoading(true);
    setInvestitiiMessage(null);
    try {
      const response = await axios.get(buildApiUrl("/api/investitii/overview"));
      const activePools = (response.data?.pools || []).filter(
        (pool) => pool.status === "active",
      );
      setInvestitiiPools(activePools);
      setInvestitiiPoolId((previous) =>
        previous || (activePools[0]?.id ? String(activePools[0].id) : ""),
      );
    } catch (error) {
      console.error("[Investitii] Nu am putut încărca pool-urile:", error);
      setInvestitiiMessage({
        type: "error",
        text:
          error.response?.data?.error ||
          "Nu am putut încărca pool-urile de investiții.",
      });
    } finally {
      setInvestitiiPoolsLoading(false);
    }
  }, []);

  const loadInvestitiiAssignment = useCallback(async (skuValue) => {
    const sku = normalizeFieldText(skuValue);
    if (!sku) {
      setInvestitiiAssignedProduct(null);
      setInvestitiiEnabled(false);
      return null;
    }

    setInvestitiiAssignmentLoading(true);
    try {
      const response = await axios.post(
        buildApiUrl("/api/investitii/products/status-by-skus"),
        { skus: [sku] },
      );
      const assigned =
        response.data?.map?.[sku.toUpperCase()] ||
        response.data?.map?.[normalizeStockSku(sku)] ||
        null;
      setInvestitiiAssignedProduct(assigned);
      if (assigned) {
        setInvestitiiEnabled(true);
        setInvestitiiPoolId((previous) =>
          previous || (assigned.poolId ? String(assigned.poolId) : ""),
        );
      } else {
        setInvestitiiEnabled(false);
      }
      return assigned;
    } catch (error) {
      console.error("[Investitii] Nu am putut verifica finanțarea SKU:", error);
      return null;
    } finally {
      setInvestitiiAssignmentLoading(false);
    }
  }, []);

  const fallbackBrandModel = fallbackBrandModelFromSummary(summary);
  const investitiiProductSnapshot = {
    sku: normalizeFieldText(effectiveCiornaSku),
    contractId: normalizeFieldText(
      nrContract || customFieldValues.customfield_10066,
    ),
    brand: normalizeFieldText(
      productDetails.brand || customFields.brand || fallbackBrandModel.brand,
    ),
    model: normalizeFieldText(
      productDetails.model ||
        customFields.model ||
        customFieldValues.customfield_10107 ||
        fallbackBrandModel.model,
    ),
    purchasePriceRon: parseRonAmount(customFieldValues.customfield_10072),
    listingPriceRon: parseRonAmount(customFieldValues.customfield_10073),
  };

  const handleToggleInvestitii = (checked) => {
    if (investitiiAssignedProduct?.assigned && !checked) {
      setInvestitiiMessage({
        type: "info",
        text: "Produsul este deja asignat în Investiții.",
      });
      return;
    }
    setInvestitiiEnabled(checked);
    setInvestitiiMessage(null);
    if (checked && !investitiiPools.length) {
      loadInvestitiiPools();
    }
  };

  const handleSaveInvestitiiProduct = async () => {
    const missing = [];
    if (!investitiiPoolId) missing.push("Finanțat din");
    if (!investitiiProductSnapshot.sku) missing.push("SKU");
    if (!investitiiProductSnapshot.contractId) missing.push("Nr contract");
    if (!investitiiProductSnapshot.brand) missing.push("Brand");
    if (!investitiiProductSnapshot.model) missing.push("Model");
    if (!investitiiProductSnapshot.purchasePriceRon) missing.push("Preț intrare");
    if (!investitiiProductSnapshot.listingPriceRon) missing.push("Preț vânzare");

    if (missing.length) {
      setInvestitiiMessage({
        type: "warning",
        text: `Completează înainte de save: ${missing.join(", ")}.`,
      });
      return;
    }

    setInvestitiiSaving(true);
    setInvestitiiMessage(null);
    try {
      const payload = {
        poolId: investitiiPoolId,
        contractId: investitiiProductSnapshot.contractId,
        jiraIssueId: issueId,
        jiraJournalNumber: investitiiProductSnapshot.contractId,
        sku: investitiiProductSnapshot.sku,
        brand: investitiiProductSnapshot.brand,
        model: investitiiProductSnapshot.model,
        purchasePriceRon: investitiiProductSnapshot.purchasePriceRon,
        allocatedAmountRon: investitiiProductSnapshot.purchasePriceRon,
        listingPriceRon: investitiiProductSnapshot.listingPriceRon,
        estimatedSalePriceRon: investitiiProductSnapshot.listingPriceRon,
        status: "in_stock",
        purchaseDate: new Date().toISOString().slice(0, 10),
        notes: `Alocat din Management Cereri / Autentificare ${issueId || ""}`,
        allocationNotes: `Finanțare salvată din cererea ${issueId || ""}`,
      };

      await axios.post(buildApiUrl("/api/investitii/products"), payload);
      await loadInvestitiiAssignment(investitiiProductSnapshot.sku);
      setInvestitiiMessage({
        type: "success",
        text: "Produsul a fost alocat în Investiții și apare în Produse finanțate.",
      });
    } catch (error) {
      console.error("[Investitii] Nu am putut aloca produsul:", error);
      if (error.response?.status === 409) {
        await loadInvestitiiAssignment(investitiiProductSnapshot.sku);
      }
      setInvestitiiMessage({
        type: error.response?.status === 409 ? "warning" : "error",
        text:
          error.response?.data?.error ||
          "Nu am putut aloca produsul în Investiții.",
      });
    } finally {
      setInvestitiiSaving(false);
    }
  };

  useEffect(() => {
    setInvestitiiMessage(null);
    loadInvestitiiAssignment(effectiveCiornaSku);
  }, [effectiveCiornaSku, loadInvestitiiAssignment]);

  useEffect(() => {
    const currentSku = normalizeInvestitiiSkuKey(effectiveCiornaSku);
    if (!currentSku) {
      return undefined;
    }

    const handleInvestitiiProductDeleted = (event) => {
      const deletedSku = normalizeInvestitiiSkuKey(event.detail?.sku);
      if (deletedSku !== currentSku) {
        return;
      }

      setInvestitiiAssignedProduct(null);
      setInvestitiiEnabled(false);
      setInvestitiiMessage({
        type: "info",
        text: "Produsul a fost șters din Produse finanțate și dezalocat.",
      });
    };

    window.addEventListener(
      INVESTITII_PRODUCT_DELETED_EVENT,
      handleInvestitiiProductDeleted,
    );

    return () =>
      window.removeEventListener(
        INVESTITII_PRODUCT_DELETED_EVENT,
        handleInvestitiiProductDeleted,
      );
  }, [effectiveCiornaSku]);

  const fetchSesiuniDisponibile = async () => {
    try {
      const response = await axios.get(
        "https://server-9p4hx.kinsta.app/api/tasks/pdp-tasks",
      );
      console.log("[📡 FETCH] fetchSesiuniDisponibile triggered");
      setSesiuniDisponibile(response.data);
      console.log("[📡 FETCH] sesiuniDisponibile:", response.data);
    } catch (error) {
      console.error("Error fetching sesiuni disponibile:", error);
    }
  };

  const handleSesiuneChange = async (event) => {
    const selectedKey = event.target.value;
    console.log("Sesiune selectată:", selectedKey);
    setSelectedSesiune(selectedKey);
    await fetchLinkedIssues(selectedKey);
  };

  const extractProductIdFromSku = (sku) => {
    if (!sku) return null;
    const numericPart = sku.replace(/\D/g, "");
    if (!numericPart) return null;
    const suffix = numericPart.slice(-6);
    return suffix ? parseInt(suffix, 10) : null;
  };

  const handleProdStatusTransition = async (
    targetStatus,
    issueKeyOverride = "",
  ) => {
    const transitionIssueId = issueKeyOverride || fisaProdusIdStatus;

    if (!transitionIssueId) {
      console.error("Fișa Produs ID este null!");
      return;
    }

    console.log(
      "Issue ID trimis pentru schimbarea statusului:",
      transitionIssueId,
    );

    try {
      const response = await axios.post(
        buildStatusApiUrl("/api/tasks/jira-transition-prod"),
        {
          issueId: transitionIssueId,
          transitionId: targetStatus,
        },
      );

      if (response.data.success) {
        console.log(`Statusul a fost schimbat cu succes la "${targetStatus}".`);
        setFisaProdusStatus(targetStatus);
        alert(`Statusul a fost schimbat cu succes la "${targetStatus}".`);
      } else {
        console.error("Eroare la schimbarea statusului.");
        alert("A apărut o eroare la schimbarea statusului.");
      }
    } catch (error) {
      console.error("Error performing status check:", error);
      alert("A apărut o eroare la schimbarea statusului.");
    }
  };

  const handleStatusCheck = async () => {
    const saved = await saveFisaProdusJiraFields({
      showSuccessAlert: false,
      showErrorAlert: true,
      resetWooStatus: false,
    });

    if (!saved) {
      return;
    }

    await handleProdStatusTransition(
      "CHECK",
      fisaProdusIdStatus || saved.fisaProdusKey || saved.fisaProdusId,
    );
    await fetchProdusStatus();
  };

  const handleStatusCiorna = async () => {
    await handleProdStatusTransition("CIORNA");
  };

  useEffect(() => {
    console.log(
      "[🧭 DEBUG] Studio Programare activ — fisaProdusId:",
      fisaProdusId,
      "| selectedSesiune:",
      selectedSesiune,
    );
  }, [fisaProdusId, selectedSesiune]);

  const fetchLinkedIssues = async (issueKey) => {
    try {
      const response = await axios.get(
        `https://server-9p4hx.kinsta.app/api/tasks/pdp-task-links/${issueKey}`,
      );
      setLinkedIssues(response.data);
    } catch (error) {
      console.error("Error fetching linked issues:", error);
    }
  };

  useEffect(() => {
    if (fisaProdusId) {
      fetchLinkedIssues(fisaProdusId);
    }
  }, [fisaProdusId]);

  // Reset studio state when SKU changes
  useEffect(() => {
    setSelectedSesiune("");
    setLinkedIssues([]);
  }, [effectiveCiornaSku]);

  useEffect(() => {
    const fetchRejectionReason = async () => {
      try {
        const response = await axios.get(
          `https://server-9p4hx.kinsta.app/api/tasks/jira-task/rejection-reason/${issueId}`,
        );
        const fetchedRejectionReason = response.data.rejectionReason || "";
        setRejectionReason(fetchedRejectionReason);
      } catch (error) {
        console.error("Error fetching rejection reason:", error);
      }
    };

    fetchRejectionReason();
  }, [issueId]);

  const handleAlocare = async (alocareData = {}) => {
    const { fotograf, sesiuneNr, sesiuneLabel } = alocareData;
    const sesiuneNumber =
      sesiuneNr ||
      (selectedSesiune && /\d+/.test(selectedSesiune)
        ? Number(selectedSesiune.match(/\d+/)[0])
        : null);

    if (!fotograf) {
      alert("Selectați fotograful (Intern / Extern).");
      return;
    }

    if (!sesiuneNumber) {
      alert(
        "Selectați o sesiune și asigurați-vă că Fișa Produs este încărcată.",
      );
      return;
    }

    const rawSku = effectiveCiornaSku;
    const selectedSku =
      typeof rawSku === "string"
        ? rawSku.trim()
        : rawSku?.value || rawSku?.id || String(rawSku || "").trim();
    if (!selectedSku) {
      alert("SKU-ul lipsește din câmpul JIRA (customfield_10083).");
      return;
    }

    const productId = extractProductIdFromSku(selectedSku);
    if (!productId) {
      alert("Nu am putut determina ID-ul produsului din SKU.");
      return;
    }

    if (!fisaProdusId) {
      alert("Fișa Produs nu este încărcată.");
      return;
    }

    try {
      const payload = {
        productId,
        denumire: summary || selectedSku,
        sku: selectedSku,
        fotograf,
        sesiuneNr: sesiuneNumber,
        finalizat: "NU",
      };

      const resAlocare = await axios.post(
        "https://server-9p4hx.kinsta.app/api/tasks/pozestudio/alocare",
        payload,
      );

      let linked = false;
      const possibleJiraKey = sesiuneLabel || "";
      const isJiraKey = /^[A-Z][A-Z0-9]+-\d+$/i.test(possibleJiraKey);
      if (isJiraKey) {
        try {
          await axios.post(
            "https://server-9p4hx.kinsta.app/api/tasks/link-tasks",
            {
              sourceTaskKey: possibleJiraKey,
              targetTaskId: fisaProdusId,
              linkType: "blocks",
            },
          );
          linked = true;
        } catch (linkErr) {
          console.warn(
            "[alocare] link-tasks skipped/fail",
            linkErr?.message || linkErr,
          );
        }
      }

      alert(
        linked
          ? "Alocarea a fost salvată și taskurile au fost legate cu succes!"
          : "Alocarea a fost salvată în backend-ul pozestudio.",
      );

      return {
        ok: true,
        allocation: resAlocare.data,
        payload,
      };
    } catch (error) {
      console.error("Error during alocare flow:", error);
      alert("A apărut o eroare la alocare sau legarea taskurilor.");
      return { ok: false, error: error?.message || String(error) };
    }
  };

  const getRenegotiationQcDetails = async () => {
    try {
      const response = await axios.get(
        buildApiUrl(`/api/qc/renegotiation-qc/${issueId}`),
      );
      const { priceOption, newPrice, currentPrice } = response.data;

      const extractedCurrentPrice =
        currentPrice || response.data.customfield_10073 || "";
      if (!extractedCurrentPrice) {
        console.warn("[⚠️] currentPrice este gol, nu îl setăm");
      } else {
        setCurrentPrice(extractedCurrentPrice);
        console.log("[✅] currentPrice setat în state:", extractedCurrentPrice);
      }

      if (priceOption?.trim() === "Propunere pret nou") {
        setPriceOption("Propunere pret nou");
        console.log(
          "[DEBUG] Renegociere activată: priceOption = Propunere pret nou",
        );
      } else {
        console.log("[DEBUG] priceOption primit:", priceOption);
      }

      setNewPrice(newPrice || "");
    } catch (error) {
      console.error("Error fetching task details:", error);
    }
  };

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const handleConditionChange = (value) => {
    setCondition(value);
  };

  const handleRejectionReasonChange = (value) => {
    setRejectionReason(value);
  };

  const handleDescriptionChange = (value) => {
    setDescription(value);
  };
  const handleSalePriceChange = (value) => {
    setCustomFieldValues((prev) => ({
      ...prev,
      customfield_10073: value,
    }));
  };

  // Rephrase description using backend helper (OpenAI proxy)
  const handleRephrase = async () => {
    try {
      setRephraseError("");
      const prompt = `Te rog sa descrii starea articolului in conformitate cu conditia: ${condition}. Menține un ton decent si clar, conform standardelor unei consignatii de lux, si include o descriere concisă: ${description}. Max. 255 caractere.`;
      const { data } = await axios.post(
        "https://server-9p4hx.kinsta.app/api/qc/openai-rephrase",
        { prompt },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      if (data && data.content) {
        setDescription(
          `${data.content} Acest articol este un produs pre-owned autentic.`,
        );
      }
    } catch (error) {
      console.error("Eroare la reformularea textului:", error);
      const backendError = error?.response?.data?.error;
      const backendDetails = error?.response?.data?.details;
      const upstreamMessage = backendDetails?.error?.message;
      const status = error?.response?.status;

      if (
        backendError === "upstream_openai_error" &&
        backendDetails?.error?.code === "invalid_api_key"
      ) {
        setRephraseError(
          "Reformularea nu este disponibilă acum: cheia OpenAI configurată pe server este invalidă."
        );
        return;
      }

      setRephraseError(
        upstreamMessage ||
          backendDetails?.message ||
          backendError ||
          (status
            ? `Serverul a răspuns cu eroarea ${status}.`
            : "Nu am putut reformula textul.")
      );
    }
  };

  // Funcția pentru preluarea valorilor disponibile și selectate pentru Badge din
  // proiectul PROD
  const fetchProdBadges = async () => {
    try {
      const response = await axios.get(
        buildApiUrl(`/api/tasks/prod-task/${encodeURIComponent(summary)}`),
      );
      const { customfield_10111 } = response.data;
      if (customfield_10111) {
        setAvailableBadges(customfield_10111.allowedValues || []);
        setSelectedBadges(
          ensureSingleBadgeSelection(customfield_10111.selectedValues),
        );
      } else {
        console.log("customfield_10111 not found in the PROD response.");
        setAvailableBadges([]);
        setSelectedBadges([]);
      }
    } catch (error) {
      console.error("Error fetching badges from PROD:", error);
    }
  };

  // Apelăm funcția pentru a obține valorile pentru Badge din PROD
  useEffect(() => {
    if (summary) {
      fetchProdBadges();
    }
  }, [summary]);

  // Funcția pentru preluarea valorilor disponibile și selectate pentru Badge-uri,
  // folosind `fisaProdusId`, similar cu `fetchAvailableTags`
  const fetchAvailableBadges = async () => {
    try {
      if (fisaProdusId) {
        const response = await axios.get(
          buildApiUrl(`/api/tasks/jira-available-badges/${fisaProdusId}`), // Endpoint pentru badge-uri, similar cu cel pentru tags
        );
        console.log("[Fisa Produs] JIRA available badges response", {
          fisaProdusId,
          data: response.data,
        });
        const { availableBadges, selectedBadges: fetchedSelectedBadges } =
          response.data;
        if (
          Array.isArray(availableBadges) &&
          Array.isArray(fetchedSelectedBadges)
        ) {
          setAvailableBadges(availableBadges);
          setSelectedBadges(ensureSingleBadgeSelection(fetchedSelectedBadges));
        } else {
          setAvailableBadges([]);
          setSelectedBadges([]);
        }
      }
    } catch (error) {
      console.error("Error fetching available badges:", error);
    }
  };

  // Apelăm funcția pentru a obține valorile pentru Badge-uri atunci când
  // `fisaProdusId` se schimbă
  useEffect(() => {
    if (fisaProdusId) {
      fetchAvailableBadges(); // Obținem badge-urile la fel cum facem pentru tags
    }
  }, [fisaProdusId]);

  useEffect(() => {
    if (fisaProdusId) {
      fetchProdAdditionalFields();
    }
  }, [fisaProdusId]);

  useEffect(() => {
    const modelValue = customFieldValues.customfield_10107;
    if (modelValue) {
      setProductDetails((prev) => ({
        ...prev,
        model: prev.model || modelValue,
      }));
    }
  }, [customFieldValues.customfield_10107]);

  const ensureSingleBadgeSelection = (badges) => {
    if (!Array.isArray(badges) || badges.length === 0) {
      return [];
    }
    const [first] = badges;
    return first ? [first] : [];
  };

  // Funcție pentru gestionarea schimbărilor din checkbox-uri
  const handleBadgeChange = (badge) => {
    setSelectedBadges((prevSelected) =>
      prevSelected.includes(badge) ? [] : [badge],
    );
  };

  const handleProductDetailFieldChange = (field, value) => {
    setProductDetails((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const fetchProdAdditionalFields = async () => {
    try {
      if (!fisaProdusId) {
        return;
      }
      const response = await axios.get(
        buildApiUrl(`/api/tasks/prod-task-fields/${fisaProdusId}`),
      );
      console.log("[Fisa Produs] PROD task fields response", {
        fisaProdusId,
        data: response.data,
      });
      const { options = {} } = response.data || {};
      setJiraProductSnapshot(initialProductDetails);
      setProductDetailOptions({
        trimisLaPozat: options.trimisLaPozat || [],
        categorieProd: options.categorieProd || [],
      });
      console.log("[Fisa Produs] PROD options loaded (details from Woo only)", {
        fisaProdusId,
        summary,
      });
    } catch (error) {
      console.error("Error fetching product detail fields from PROD:", error);
    } finally {
      setHasLoadedJiraSnapshot(true);
    }
  };

  const handleTagsChange = (newTags) => {
    setSelectedTags(Array.isArray(newTags) ? newTags : []);
  };

  const buildFisaProdusUpdatePayload = () => {
    const normalizedTags = (selectedTags || [])
      .map((tag) => (typeof tag === "string" ? tag : tag?.value))
      .filter(Boolean);
    const normalizedBadges = (selectedBadges || [])
      .map((badge) => (typeof badge === "string" ? badge : badge?.value))
      .filter(Boolean);
    const {
      trimisLaPozat,
      culoareHW,
      categorieProd,
      subcategorie,
      brand,
      marimeDimensiune,
      bustDimensiune,
      sleeveDimensiune,
      shoulderDimensiune,
      culoareExterioara,
      culoareInterioara,
      materialExterior,
      materialInterior,
      designerId,
      entrupyId,
      model,
      location,
      msrpPrice,
      wooCategoriesSelected,
    } = productDetails;
    const rawPrice = customFieldValues?.customfield_10073;
    const priceValue =
      rawPrice === undefined || rawPrice === null
        ? ""
        : String(rawPrice).trim();

    return {
      normalizedTags,
      normalizedBadges,
      priceValue,
      updatePayload: {
        condition,
        description,
        selectedTags: normalizedTags,
        customfield_10111: normalizedBadges,
        customfield_10073: priceValue || null,
        model,
        trimisLaPozat,
        culoareHW,
        categorieProd,
        subcategorie,
        brand,
        marimeDimensiune,
        bustDimensiune,
        sleeveDimensiune,
        shoulderDimensiune,
        culoareExterioara,
        culoareInterioara,
        materialExterior,
        materialInterior,
        designerId,
        entrupyId,
        location,
        msrpPrice,
        wooCategoriesSelected,
      },
    };
  };

  const saveFisaProdusJiraFields = async ({
    showSuccessAlert = false,
    showErrorAlert = true,
    resetWooStatus = false,
  } = {}) => {
    try {
      console.error("[DEBUG SAVE] handleSaveConditionAndDescription START", {
        issueId,
        fisaProdusId,
        syncToPlatform,
        skuFromCustomField: customFieldValues?.customfield_10083 || "",
        skuJira: skuJira || "",
      });
      if (resetWooStatus) {
        setWooSyncStatus({ message: "", severity: "success" });
      }
      let ensuredFisaProdusId = fisaProdusId;
      let ensuredFisaProdusKey = fisaProdusIdStatus;
      if (!ensuredFisaProdusId) {
        console.warn(
          "[Fișa Produs] fisaProdusId lipsă, reîncerc să îl obțin din PROD.",
        );
        const refreshed = await fetchProdusStatus();
        ensuredFisaProdusId = refreshed?.id || null;
        ensuredFisaProdusKey = refreshed?.key || ensuredFisaProdusKey;
      }

      if (!ensuredFisaProdusId) {
        const message =
          "Nu am reușit să identific Fișa Produs în JIRA pentru acest summary. Verifică dacă există task în PROD.";
        console.error(
          "[Fișa Produs] Nu am putut determina fisaProdusId, opresc salvarea.",
        );
        if (showErrorAlert) {
          alert(message);
        }
        return null;
      }

      const payloadData = buildFisaProdusUpdatePayload();

      console.log("[Fișa Produs] Trimitem update către backend:", {
        fisaProdusId: ensuredFisaProdusId,
        updatePayload: payloadData.updatePayload,
      });

      const updateResponse = await axios.put(
        buildApiUrl(`/api/tasks/update-prod-task/${ensuredFisaProdusId}`),
        payloadData.updatePayload,
      );
      console.error("[DEBUG SAVE] Jira PUT done", {
        status: updateResponse?.status,
        fisaProdusId: ensuredFisaProdusId,
      });

      console.log("Response from backend:", updateResponse.data);

      if (updateResponse.status === 200) {
        console.log(
          "Condition, description, badge, and tags saved successfully in PROD.",
        );
        if (showSuccessAlert) {
          alert("Datele au fost salvate cu succes!");
        }
        setFisaProdusId(ensuredFisaProdusId);
        if (ensuredFisaProdusKey) {
          setFisaProdusIdStatus(ensuredFisaProdusKey);
        }
        return {
          ...payloadData,
          fisaProdusId: ensuredFisaProdusId,
          fisaProdusKey: ensuredFisaProdusKey,
          updateResponse,
        };
      }

      console.error(
        "Failed to save condition, description, badge, and tags in PROD.",
      );
      if (showErrorAlert) {
        alert("Eroare la salvarea datelor.");
      }
      return null;
    } catch (error) {
      console.error(
        "Error saving condition, description, badge, and tags in PROD:",
        error.response?.status,
        error.response?.data || error.message,
      );
      if (showErrorAlert) {
        alert("A apărut o eroare la salvarea datelor.");
      }
      return null;
    }
  };

  // Funcția pentru salvarea tuturor datelor (inclusiv badge-urile)
  const handleSaveConditionAndDescription = async () => {
    const saveResult = await saveFisaProdusJiraFields({
      showSuccessAlert: true,
      showErrorAlert: true,
      resetWooStatus: true,
    });

    if (!saveResult) {
      return;
    }

    const skuForWoo = (
      customFieldValues.customfield_10083 ||
      skuJira ||
      ""
    ).trim();
    if (syncToPlatform && skuForWoo) {
      try {
        await syncWooProductDetails({
          sku: skuForWoo,
          conditionValue: condition,
          formattedDescription: description,
          normalizedTags: saveResult.normalizedTags,
          normalizedBadges: saveResult.normalizedBadges,
          details: productDetails,
          priceValue: saveResult.priceValue,
        });
        setWooSyncStatus({
          message: "Salvare în WooCommerce reușită.",
          severity: "success",
        });
      } catch (wooError) {
        console.error(
          "[Fișa Produs] WooCommerce sync failed:",
          wooError?.response?.data || wooError.message,
        );
        setWooSyncStatus({
          message: "Eroare la salvarea în WooCommerce. Verifică logurile.",
          severity: "error",
        });
      }
    } else if (!skuForWoo) {
      console.warn(
        "[Fișa Produs] SKU lipsă după salvarea în PROD, nu sincronizăm platforma.",
      );
      if (syncToPlatform) {
        setWooSyncStatus({
          message: "SKU lipsă. Nu am putut sincroniza WooCommerce.",
          severity: "error",
        });
      }
    }
  };

  // Funcție pentru reîncărcarea produselor din stoc (Sortly) pentru acest summary
  const fetchStockProducts = useCallback(async (skuOverride = "") => {
    const ciornaSkuForStock =
      typeof skuOverride === "string" && skuOverride.trim()
        ? skuOverride.trim()
        : getCiornaSkuForStock();
    const normalizedCiornaSku = normalizeStockSku(ciornaSkuForStock);

    if (!normalizedCiornaSku) {
      console.warn(
        "[📦 FETCH] SKU Ciorna JIRA 10083 lipsește. Nu căutăm produse Sortly.",
      );
      setStockProducts([]);
      return;
    }

    console.log("[📦 FETCH] Căutăm produse Sortly pentru:", {
      ciornaSku: ciornaSkuForStock,
    });

    try {
      const { data } = await axios.get(
        buildApiUrl("/api/stocmanager/products-search"),
        {
          params: {
            q: ciornaSkuForStock,
            limit: 25,
          },
        },
      );

      const results = Array.isArray(data?.results) ? data.results : [];

      const matchingProducts = results.filter((product) => {
        const productSku = normalizeStockSku(product.sku);
        return productSku === normalizedCiornaSku;
      });

      if (matchingProducts.length > 0) {
        console.log(
          "[✅] Produse existente în stock manager cu SKU exact:",
          matchingProducts,
        );
        setStockProducts(matchingProducts);
      } else {
        console.warn(
          "[⚠️] Nu există produs Sortly cu SKU exact pentru cererea curentă.",
        );
        setStockProducts([]);
      }
    } catch (err) {
      console.error("❌ Eroare la preluarea produselor din stoc:", err);
      setStockProducts([]);
    }
  }, [getCiornaSkuForStock]);

  useEffect(() => {
    fetchStockProducts(effectiveCiornaSku);
  }, [effectiveCiornaSku, fetchStockProducts]);

  // Funcție pentru adăugare în stoc (Sortly) folosind imaginea principală
  const handleAddToStock = async () => {
    try {
      const ciornaSkuForStock = getCiornaSkuForStock();
      if (!normalizeStockSku(ciornaSkuForStock)) {
        alert(
          "Nu poți adăuga produsul în stoc fără SKU Ciorna salvat în JIRA 10083.",
        );
        return;
      }

      console.log(
        "🧪 Pornim procesul de obținere imagine principală pentru adăugare în stoc...",
      );
      const imageBlob = await getMainProductImageBlob();
      if (!imageBlob) {
        console.error(
          "❌ Nu s-a putut obține imaginea principală din getMainProductImageBlob",
        );
        alert("Imaginea pentru produs nu este disponibilă.");
        return;
      }

      const formData = new FormData();
      formData.append("product_name", summary);
      formData.append("folder_id", 3);
      formData.append("quantity", 1);
      formData.append("price", customFieldValues.customfield_10073 || 0);
      formData.append("sku", ciornaSkuForStock);
      formData.append("image", imageBlob, "product.jpg");

      // Logging before sending to API
      console.log("🧾 Adăugare în stoc (Sortly):");
      console.log("➡️ Nume produs:", summary);
      console.log("➡️ Folder ID:", 3);
      console.log("➡️ Cantitate:", 1);
      console.log("➡️ Preț:", customFieldValues.customfield_10073 || 0);
      console.log("➡️ SKU Ciorna JIRA 10083:", ciornaSkuForStock);
      if (imageBlob) {
        console.log("📷 Imagine găsită:", imageBlob);
        console.log("📷 Imagine tip:", imageBlob.type);
        console.log("📷 Imagine size:", imageBlob.size, "bytes");
      } else {
        console.warn("⚠️ Imagine indisponibilă pentru produs.");
      }

      const sortlyResponse = await axios.post(
        buildApiUrl("/api/stocmanager/products/upload-image"),
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      console.log("✅ Răspuns backend Sortly:", sortlyResponse.data);
      alert("Produsul a fost adăugat în stoc (Sortly) cu succes!");
      const insertId = sortlyResponse.data?.insertId;

      if (insertId) {
        try {
          const { data: createdProduct } = await axios.get(
            buildApiUrl(`/api/stocmanager/products/${insertId}`),
          );
          if (createdProduct) {
            if (
              normalizeStockSku(createdProduct.sku) !==
              normalizeStockSku(ciornaSkuForStock)
            ) {
              console.warn(
                "[⚠️] Produsul Sortly creat nu are SKU-ul cerut, nu îl afișăm.",
                {
                  expectedSku: ciornaSkuForStock,
                  createdSku: createdProduct.sku,
                },
              );
              await fetchStockProducts();
              return;
            }
            setStockProducts((prev) => {
              const filtered = prev.filter(
                (product) => product.id !== createdProduct.id,
              );
              return [createdProduct, ...filtered];
            });
          } else {
            await fetchStockProducts();
          }
        } catch (fetchNewProductError) {
          console.warn(
            "[⚠️] Nu am putut încărca produsul nou creat, reiau fetch general.",
            fetchNewProductError,
          );
          await fetchStockProducts();
        }
      } else {
        await fetchStockProducts();
      }
    } catch (error) {
      console.error("Eroare la adăugarea în stoc (Sortly):", error);
      console.log("📛 Detalii eroare backend:", error.response?.data);
      alert("A apărut o eroare la adăugarea în stoc (Sortly).");
    }
  };

  const handleQcStatusChange = async (status) => {
    try {
      const updatePayload = {
        customfield_10188: status,
      };

      if (status === "Respins") {
        updatePayload.customfield_10190 = rejectionReason;
      }

      await axios.put(
        `https://server-9p4hx.kinsta.app/api/tasks/jira-task/${issueId}`,
        updatePayload,
      );

      qcStatusRef.current = status;
      setQcStatus(status);
      onUpdateCustomField(status);
    } catch (error) {
      console.error("Error updating QC status:", error);
    }
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleReportClick = () => {
    setOpenReportModal(true);
    handleMenuClose();
  };

  const handleCloseReportModal = () => {
    setOpenReportModal(false);
  };

  const getCardStyle = () => {
    switch (qcStatus) {
      case "Acceptat":
        return { backgroundColor: "rgba(0, 255, 0, 0.1)" };
      case "Respins":
        return { backgroundColor: "rgba(255, 255, 0, 0.1)" };
      case "Fake":
        return { backgroundColor: "rgba(255, 0, 0, 0.1)" };
      default:
        return {};
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAwbData({
      ...awbData,
      [name]: value,
    });
  };

  const handleGenerateAwbAndReport = async () => {
    try {
      const strictAwbStatus = qcStatus === "Fake" || qcStatus === "Restituit";
      const autoRenegotiationReasonActive =
        showRenegotiation && qcStatus === "Respins";
      const renegotiationAutoReason =
        "Propunerea de preț înaintată în cadrul procesului de renegociere nu a fost acceptată, astfel produsul rămâne respins în urma verificării Quality Check.";
      const derivedRejectionReason = autoRenegotiationReasonActive
        ? renegotiationAutoReason
        : rejectionReason;
      if (qcStatus === "Respins" && !derivedRejectionReason?.trim()) {
        alert(
          "Selectează motivul pentru respingere înainte de generarea PDF-ului.",
        );
        return;
      }
      if (strictAwbStatus) {
        const requiredFields = [
          "street",
          "number",
          "postalCode",
          "city",
          "county",
        ];
        const missingFields = requiredFields.filter(
          (field) => !String(awbData?.[field] || "").trim(),
        );

        if (missingFields.length > 0) {
          alert(
            "Te rog completează toate câmpurile obligatorii pentru AWB (Strada, Nr, Cod posta" +
              "l, Oraș, Județ) înainte de generare.",
          );
          return;
        }
      }

      const finalAwbData = {
        ...awbData,
        contactName: awbData.contactName || contactName,
        contactPhone: awbData.contactPhone || contactPhone,
        contactEmail: awbData.contactEmail || contactEmail,
      };

      let endpoint = "create-awb-qc";
      if (qcStatus === "Fake") {
        endpoint = "create-awb-qc-fake";
      } else if (qcStatus === "Restituit") {
        endpoint = "create-awb-qc-restituit";
      }
      const response = await axios.post(
        `https://server-9p4hx.kinsta.app/api/tasks/${endpoint}`,
        {
          ...finalAwbData,
          issueId,
          category,
          rejectionReason:
            qcStatus === "Respins" ? derivedRejectionReason : undefined,
        },
      );

      if (response.status === 200) {
        console.log("AWB și raport generate cu succes");

        const { awbNumber, awbPdf, reportPdf, pdfFilename } = response.data;

        // Download AWB PDF
        const awbFileName = `AWB_${qcStatus}_${awbNumber}.pdf`;
        const awbBlob = new Blob(
          [Uint8Array.from(atob(awbPdf), (c) => c.charCodeAt(0))],
          { type: "application/pdf" },
        );
        const awbUrl = URL.createObjectURL(awbBlob);
        const awbLink = document.createElement("a");
        awbLink.href = awbUrl;
        awbLink.setAttribute("download", awbFileName);
        document.body.appendChild(awbLink);
        awbLink.click();
        awbLink.parentNode.removeChild(awbLink);

        // Download Report PDF
        const notaReturBlob = new Blob(
          [Uint8Array.from(atob(reportPdf), (c) => c.charCodeAt(0))],
          { type: "application/pdf" },
        );
        const notaReturUrl = URL.createObjectURL(notaReturBlob);
        const notaReturLink = document.createElement("a");
        notaReturLink.href = notaReturUrl;
        notaReturLink.setAttribute("download", pdfFilename);
        document.body.appendChild(notaReturLink);
        notaReturLink.click();
        notaReturLink.parentNode.removeChild(notaReturLink);

        // Send SMS to the client
        try {
          const smsEndpoint =
            qcStatus === "Respins"
              ? "send-respins-sms"
              : "send-awb-respins-sms";

          const smsPayload =
            qcStatus === "Respins"
              ? {
                  contactName: finalAwbData.contactName,
                  contactPhone: finalAwbData.contactPhone,
                  rejectionReason,
                }
              : {
                  contactName: finalAwbData.contactName,
                  contactPhone: finalAwbData.contactPhone,
                };

          const smsResponse = await axios.post(
            `https://server-9p4hx.kinsta.app/api/tasks/${smsEndpoint}`,
            smsPayload,
          );

          if (smsResponse.status === 200) {
            console.log("AWB/report SMS sent successfully");
          } else {
            console.error(
              "Failed to send AWB/report SMS:",
              smsResponse.statusText,
            );
          }
        } catch (smsError) {
          console.error(
            "Error sending AWB/report SMS:",
            smsError.message || smsError,
          );
        }
      } else {
        console.error("Failed to create AWB and report");
      }
    } catch (error) {
      console.error("Error creating AWB and report:", error);
    }
  };

  // Sincronizează SKU-ul din parent (TaskEdit) dacă este disponibil
  useEffect(() => {
    if (skuJira) {
      setCustomFieldValues((prev) => ({
        ...prev,
        customfield_10083: skuJira,
      }));
    }
  }, [skuJira]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          `https://server-9p4hx.kinsta.app/api/tasks/jira-task/${issueId}`,
        );
        const taskData = response.data;

        console.log("Fetched task data:", taskData); // Verifică ce date sunt primite

        setCustomFieldValues({
          customfield_10066:
            taskData.customfield_10066 || taskData.uniqueId || "",
          customfield_10083:
            taskData.customfield_10083?.value ||
            taskData.customfield_10083 ||
            "",
          customfield_10077: taskData.customfield_10077 || "",
          customfield_10072: taskData.customfield_10072 || "",
          customfield_10073: taskData.customfield_10073 || "",
          customfield_10080: taskData.customfield_10080 || "",
          customfield_10183: taskData.customfield_10183 || "",
          customfield_10173: taskData.customfield_10173 || "",
          customfield_10078: taskData.customfield_10078 || "",
          customfield_10165: taskData.customfield_10165 || "",
          customfield_10185: taskData.customfield_10185 || "",
          customfield_10067: taskData.customfield_10067 || "",
          customfield_10069: taskData.customfield_10069 || "",
          customfield_10068: taskData.customfield_10068 || "",
          customfield_10186: taskData.customfield_10186 || "",
          customfield_10188: taskData.customfield_10188 || "",
          customfield_10107: taskData.customfield_10107 || "",
        });

        const customFields = [
          "customfield_10161",
          "customfield_10160",
          "customfield_10080",
          "customfield_10183",
          "customfield_10173",
          "customfield_10165",
          "customfield_10185",
        ];
        const options = {};

        for (const field of customFields) {
          const customFieldResponse = await axios.get(
            `https://server-9p4hx.kinsta.app/api/tasks/jira-custom-field-options/${field}`,
          );
          options[field] = customFieldResponse.data;
          console.log(`Options for ${field}:`, customFieldResponse.data); // Verifică opțiunile returnate pentru fiecare câmp
        }

        setCustomFieldOptions(options);
      } catch (error) {
        console.error("There was an error fetching the task data!", error);
        setError("There was an error fetching the task data!");
      }
    };

    fetchData();
  }, [issueId]);

  // Pentru zona de produse din stock manager importăm useState, useEffect deja
  // existente State pentru produsele din stock manager (Sortly)
  const [stockProducts, setStockProducts] = useState([]);

  // useEffect pentru a prelua lista produselor din stock manager (Sortly) pentru
  // acest summary
  useEffect(() => {
    if (summary) {
      console.log("[🔄 useEffect] summary changed:", summary);
      resetState(); // Ensure state is reset when summary changes
      fetchProdusStatus();
      console.log("[🔁 Resummary triggered] summary:", summary);
      fetchStockProducts(); // Always re-fetch stock products on summary change
    }
  }, [summary]);

  const fetchCustomFields = async () => {
    try {
      const response = await axios.get(
        `https://server-9p4hx.kinsta.app/api/tasks/jira-task/${issueId}`,
      );
      const data = response.data;

      setCustomFieldValues({
        customfield_10066: data.customfield_10066 || "",
        customfield_10083: data.customfield_10083 || "",
        customfield_10077: data.customfield_10077 || "",
        customfield_10072: data.customfield_10072 || "",
        customfield_10073: data.customfield_10073 || "",
        customfield_10080: data.customfield_10080 || "",
        customfield_10183: data.customfield_10183 || "",
        customfield_10173: data.customfield_10173 || "",
        customfield_10078: data.customfield_10078 || "",
        customfield_10165: data.customfield_10165 || "",
        customfield_10185: data.customfield_10185 || "",
        customfield_10111: data.customfield_10111 || [], // Adăugăm aici noul customfield pentru Badge
        customfield_10186: data.customfield_10186 || "",
      });

      setCustomFieldOptions({
        customfield_10080: data.customfield_10080_options || [],
        customfield_10183: data.customfield_10183_options || [],
        customfield_10173: data.customfield_10173_options || [],
        customfield_10165: data.customfield_10165_options || [],
        customfield_10111: data.customfield_10111_options || [], // Adăugăm opțiunile pentru customfield_10111
        customfield_10185: data.customfield_10185_options || [],
      });
    } catch (error) {
      console.error("Error fetching custom fields:", error);
    }
  };

  useEffect(() => {
    if (issueId) {
      fetchCustomFields();
    }
  }, [issueId]);

  useEffect(() => {
    console.log("customFieldOptions:", customFieldOptions);
  }, [customFieldOptions]);

  const handleUnlinkTask = async (sourceTaskKey, targetTaskKey) => {
    if (!sourceTaskKey || !targetTaskKey) {
      console.error("sourceTaskKey sau targetTaskKey este gol!");
      return;
    }

    try {
      const response = await axios.post(
        "https://server-9p4hx.kinsta.app/api/tasks/unlink-tasks",
        { sourceTaskKey, targetTaskKey },
      );

      console.log("Unlink response:", response.data);

      setLinkedIssues((prevIssues) =>
        prevIssues.filter((issue) => issue.key !== targetTaskKey),
      );
    } catch (error) {
      console.error("Error unlinking tasks:", error);
    }
  };

  return (
    <>
      {" "}
      <Backdrop
        open={isCreatingDraft}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.modal + 10,
          bgcolor: "rgba(17, 24, 39, 0.72)",
          backdropFilter: "blur(2px)",
        }}
      >
        <Box
          sx={{
            px: 4,
            py: 3,
            borderRadius: 2,
            bgcolor: "rgba(17, 24, 39, 0.88)",
            boxShadow: "0 18px 50px rgba(0, 0, 0, 0.35)",
          }}
        >
          <Stack spacing={2} alignItems="center">
            <CircularProgress color="inherit" />
            <Typography fontWeight={800}>Se creează ciorna...</Typography>
            <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.78)" }}>
              Clonăm produsul și atașăm imaginea principală.
            </Typography>
          </Stack>
        </Box>
      </Backdrop>
      <QcCard
        issueId={issueId}
        cardStyle={getCardStyle()}
        qcStatus={qcStatus}
        anchorEl={anchorEl}
        onMenuClick={handleMenuClick}
        onMenuClose={handleMenuClose}
        onReportClick={handleReportClick}
        openReportModal={openReportModal}
        onCloseReportModal={handleCloseReportModal}
        currentPrice={currentPrice}
        newPrice={newPrice}
        acceptedPrice={acceptedRenegotiationPrice}
        fallbackPrice={customFieldValues.customfield_10072}
        onChangeNewPrice={(value) => {
          setNewPrice(value);
          setAdditionalActFeedback(null);
        }}
        onSaveNewPrice={handleSaveNewPrice}
        isAcceptingPrice={isAcceptingRenegotiationPrice}
        onRejectPrice={handleRejectPrice}
        jiraFields={jiraFields}
        vendorData={effectiveVendorData}
        onCreateActAditional={handleCreareActAditional}
        isCreatingAct={isCreatingAdditionalAct}
        creationFeedback={additionalActFeedback}
        additionalActPreview={additionalActPreview}
        onChangeStatus={handleQcStatusChange}
        expanded={expanded}
        onToggleExpand={handleExpandClick}
        rejectionReason={rejectionReason}
        onChangeRejectionReason={handleRejectionReasonChange}
        awbData={awbData}
        onAwbInputChange={handleInputChange}
        onGenerateAwbAndReport={handleGenerateAwbAndReport}
        showRenegotiation={priceOption?.trim() === "Propunere pret nou"}
      />{" "}
      <ManagementStockCard
        stockProducts={stockProducts}
        onRefresh={() => fetchStockProducts(effectiveCiornaSku)}
        onAddToStock={handleAddToStock}
        expectedSku={effectiveCiornaSku}
      />{" "}
      <DraftProductCard
        ciornaPlatforma={ciornaPlatforma}
        onChangeCiornaPlatforma={setCiornaPlatforma}
        onSaveCiornaPlatforma={handleSaveCiornaPlatforma}
        wooCommerceTitle={wooCommerceTitle}
        wooCommerceStatus={wooCommerceStatus}
        customFields={customFields}
        onFilter={handleFilter}
        filteredProducts={filteredProducts}
        filteredProductsLoading={filteredProductsLoading}
        filteredProductsError={filteredProductsError}
        selectedProductIds={selectedProductIds}
        onSelectProduct={handleProductSelect}
        showCreateProduct={showCreateProduct}
        productData={productData}
        onCreateDraftClick={handleCreateDraftClick}
        isCreatingDraft={isCreatingDraft}
      />{" "}
      <FisaProdusCard
        fisaProdusStatus={fisaProdusStatus}
        condition={condition}
        onChangeCondition={handleConditionChange}
        description={description}
        onChangeDescription={handleDescriptionChange}
        salePrice={customFieldValues.customfield_10073}
        onChangeSalePrice={handleSalePriceChange}
        onRephrase={handleRephrase}
        availableTags={availableTags}
        selectedTags={selectedTags}
        onChangeTags={handleTagsChange}
        availableBadges={availableBadges}
        selectedBadges={selectedBadges}
        onToggleBadge={handleBadgeChange}
        productDetails={productDetails}
        onChangeProductDetail={handleProductDetailFieldChange}
        productDetailOptions={productDetailOptions}
        onSave={handleSaveConditionAndDescription}
        onStatusCheck={handleStatusCheck}
        onStatusCiorna={handleStatusCiorna}
        syncToPlatform={syncToPlatform}
        onToggleSyncDestination={setSyncToPlatform}
        issueId={issueId}
        summary={summary}
        sku={skuJira || customFieldValues.customfield_10083}
        onRequestWooRefresh={handleRequestWooRefresh}
        isWooLoading={isWooDetailsLoading}
        isInitialFisaLoading={isInitialFisaLoading}
        rephraseError={rephraseError}
        wooSyncStatus={wooSyncStatus}
        onClearWooSyncStatus={() =>
          setWooSyncStatus({ message: "", severity: "success" })
        }
      />{" "}
      <InvestitiiFinantareCard
        enabled={investitiiEnabled}
        poolId={investitiiPoolId}
        pools={investitiiPools}
        loadingPools={investitiiPoolsLoading}
        saving={investitiiSaving}
        checkingAssigned={investitiiAssignmentLoading}
        message={investitiiMessage}
        assignedProduct={investitiiAssignedProduct}
        productSnapshot={investitiiProductSnapshot}
        canSave={Boolean(
          investitiiEnabled &&
            investitiiPoolId &&
            !investitiiAssignedProduct?.assigned,
        )}
        onToggleEnabled={handleToggleInvestitii}
        onChangePoolId={(value) => setInvestitiiPoolId(String(value || ""))}
        onSave={handleSaveInvestitiiProduct}
      />{" "}
      <StudioProgramareCard
        key={effectiveCiornaSku || "studio-card"}
        selectedSesiune={selectedSesiune}
        onChangeSesiune={handleSesiuneChange}
        sesiuniDisponibile={sesiuniDisponibile}
        linkedIssues={linkedIssues}
        fisaProdusId={fisaProdusId}
        onUnlinkTask={handleUnlinkTask}
        onAlocare={handleAlocare}
        skuJira={effectiveCiornaSku}
      />{" "}
    </>
  );
}
